/* =============================================================================
 * The words on a document, in both languages
 * -----------------------------------------------------------------------------
 * A fixed, finite set: the headings and labels this system prints. Nothing here
 * is generated, and nothing that came from a person is translated — a name, a
 * site, a note somebody typed stays exactly as it was written.
 *
 * That distinction is the whole discipline of this file. Translating "Gross
 * earning" is safe because it means one thing and the translation was checked
 * once. Translating "Noor Ul Amin" produces a spelling that is close, plausible
 * and not the one on his passport, which is the only spelling that matters on a
 * document he may have to show somebody.
 *
 * -----------------------------------------------------------------------------
 * Adding to this
 * -----------------------------------------------------------------------------
 * An English key with no Arabic beside it falls back to the English, so a label
 * added in a hurry prints in English rather than blank. That is deliberate and
 * it is also how this file rots: a document half in each language reads worse
 * than one wholly in either. docStrings.test.ts fails when a key is missing, so
 * the gap is visible rather than merely survivable.
 * ========================================================================== */

/* Relative, and with extensions. This dictionary is imported by the daily
   attendance email, which is built inside an Edge Function: Deno resolves no
   aliases and no extensionless paths, and a deploy refuses the whole project
   over either. See theDayInOneEmail. */
import { formatDateAs, monthName } from '../dateFormats.ts';
import { DIRHAM_SIGN, isDirham } from '../dirham.ts';

export type DocLang = 'en' | 'ar';

/* Deliberately not `as const`.
   With literal types TypeScript proves every Arabic value non-empty and calls
   the English fallback unreachable — true of the table as it stands today, and
   exactly the assumption that breaks the moment somebody adds a key in a hurry.
   Without it the values are plain strings, so the fallback is reachable code,
   while `keyof typeof` still gives the real keys and a typo in a caller is
   still a compile error. */
export const DOC_STRINGS = {
  // ---- document titles ----------------------------------------------------
  payslip: { en: 'Payslip', ar: 'قسيمة الراتب' },
  salarySummary: { en: 'Salaries & overtime summary', ar: 'ملخص الرواتب والعمل الإضافي' },
  jobCost: { en: 'Job cost', ar: 'تكلفة المشروع' },
  settlement: { en: 'Final settlement', ar: 'التسوية النهائية' },
  attendance: { en: 'Attendance', ar: 'الحضور' },

  // ---- the person ---------------------------------------------------------
  employee: { en: 'Employee', ar: 'الموظف' },
  employeeId: { en: 'Employee ID', ar: 'رقم الموظف' },
  designation: { en: 'Designation', ar: 'المسمى الوظيفي' },
  joinedOn: { en: 'Joined on', ar: 'تاريخ الالتحاق' },
  lastWorkingDay: { en: 'Last working day', ar: 'آخر يوم عمل' },
  nationality: { en: 'Nationality', ar: 'الجنسية' },

  // ---- days ---------------------------------------------------------------
  daysPaid: { en: 'Days paid', ar: 'الأيام المدفوعة' },
  daysWorked: { en: 'Days worked', ar: 'أيام العمل' },
  absent: { en: 'Absent', ar: 'غائب' },
  leave: { en: 'Leave', ar: 'إجازة' },
  holiday: { en: 'Holiday', ar: 'عطلة' },
  overtimeHours: { en: 'Overtime hours', ar: 'ساعات العمل الإضافي' },

  // ---- money --------------------------------------------------------------
  perDayWage: { en: 'Per day wage', ar: 'أجر اليوم' },
  ratePerHour: { en: 'Rate per hour', ar: 'أجر الساعة' },
  salary: { en: 'Salary', ar: 'الراتب' },
  overtime: { en: 'Overtime', ar: 'العمل الإضافي' },
  additions: { en: 'Additions', ar: 'الإضافات' },
  deductions: { en: 'Deductions', ar: 'الاستقطاعات' },
  grossEarning: { en: 'Gross earning', ar: 'إجمالي الأجر' },
  alreadyPaid: { en: 'Pre-payment', ar: 'دفعة مقدمة' },
  netPayable: { en: 'Net payable', ar: 'صافي المستحق' },
  advanceRecovery: { en: 'Advance recovery', ar: 'استرداد السلفة' },
  gratuity: { en: 'Gratuity', ar: 'مكافأة نهاية الخدمة' },
  leaveSalary: { en: 'Leave Salary', ar: 'بدل الإجازة' },
  ticket: { en: 'Air ticket', ar: 'تذكرة السفر' },

  // ---- the page itself ----------------------------------------------------
  total: { en: 'Total', ar: 'الإجمالي' },
  date: { en: 'Date', ar: 'التاريخ' },
  site: { en: 'Site', ar: 'الموقع' },
  jobNo: { en: 'Job no.', ar: 'رقم المشروع' },
  computerGenerated: {
    en: 'This is a computer-generated statement',
    ar: 'هذا كشف صادر عن نظام آلي',
  },
};

export type DocKey = keyof typeof DOC_STRINGS;

/**
 * A label in the document's language.
 *
 * Falls back to English rather than to nothing: a heading that has not been
 * translated yet should read oddly, not vanish. A blank column heading on a
 * payslip is a document somebody cannot check.
 */
export function docText(key: DocKey, lang: DocLang): string {
  const entry = DOC_STRINGS[key];
  return (lang === 'ar' ? entry.ar : entry.en) || entry.en;
}

/**
 * Both, in reading order for the page.
 *
 * Used where a heading has room for the pair — a section band, a title — so a
 * document is legible to somebody who reads only one of the two without
 * needing a second copy of the whole page.
 */
export function docBoth(key: DocKey): string {
  const entry = DOC_STRINGS[key];
  return entry.ar ? `${entry.en}  ·  ${entry.ar}` : entry.en;
}

/* =============================================================================
 * Column headings and the words this system generates
 * -----------------------------------------------------------------------------
 * Every list in the app can be exported, and each one names its own columns in
 * English at the call site. Those headings are not free text — they are a fixed
 * vocabulary of about a hundred words that this codebase chose, so they can be
 * translated the same way the payslip labels are.
 *
 * Looked up on the exact English string. That matters more than it looks: an
 * exact match can only ever hit a heading this file already knows, so a cell
 * holding a person's name, a site, a plate number or a typed remark cannot be
 * caught by accident. A fuzzy match would eventually translate somebody's name,
 * which is the one thing on a document that must survive untouched.
 *
 * A heading with no entry prints in English. That is visible, and preferable to
 * a blank column on a document somebody has to check.
 * ========================================================================== */

const DOC_LABELS: Record<string, string> = {
  /* The letters the company issues. Shown in the picker, in the list of what
     has been issued to somebody, and on the document itself — see 0153. */
  offer_letter: 'عرض عمل',
  employment_contract: 'عقد عمل',
  /* فترة التجربة is the term in the law and the term 'Probation ends' uses
     below. This letter called it فترة الاختبار, so the certificate and the
     staff file named the same period two different things. */
  probation_confirmation: 'تثبيت بعد فترة التجربة',
  salary_certificate: 'شهادة راتب',
  salary_transfer_letter: 'خطاب تحويل راتب',
  employment_verification: 'إفادة بالعمل',
  noc_general: 'شهادة عدم ممانعة',
  noc_travel: 'عدم ممانعة للسفر',
  noc_driving_licence: 'عدم ممانعة لرخصة قيادة',
  leave_approval: 'موافقة على إجازة',
  air_ticket_entitlement: 'استحقاق تذكرة سفر',
  promotion_letter: 'خطاب ترقية',
  warning_letter: 'إنذار خطي',
  resignation_acceptance: 'قبول استقالة',
  termination_letter: 'إنهاء خدمة',
  experience_certificate: 'شهادة خبرة',
  relieving_letter: 'إخلاء طرف',
  /* The letter's own title says طلب إلغاء تأشيرة إقامة, and it is the
     residence visa that gets cancelled — تأشيرة alone is the entry visa. */
  visa_cancellation_request: 'طلب إلغاء تأشيرة إقامة',

  // ---- who and what -------------------------------------------------------
  Employee: 'الموظف',
  'Employee code': 'رمز الموظف',
  Name: 'الاسم',
  ID: 'الرقم',
  Designation: 'المسمى الوظيفي',
  Nationality: 'الجنسية',
  Role: 'الصلاحية',
  Phone: 'الهاتف',
  Project: 'المشروع',
  Client: 'العميل',
  Vehicle: 'المركبة',
  Driver: 'السائق',
  Plate: 'رقم اللوحة',
  /* The windscreen card. "امسح" is the scan a camera does, and استلام is
     taking charge of the vehicle rather than merely borrowing it — which is
     what the claim records. See vehicleCardPdf. */
  'Scan to take this vehicle': 'امسح لاستلام هذه المركبة',
  'No vehicle has a card issued yet.': 'لم تُصدر بطاقة لأي مركبة بعد.',
  Device: 'الجهاز',
  Handset: 'الهاتف',
  Document: 'المستند',
  Documents: 'المستندات',
  Subject: 'الموضوع',
  Title: 'العنوان',
  Type: 'النوع',
  Status: 'الحالة',
  /* The two rows of the payslip strip that say when a day began and ended. */
  In: 'حضور',
  Out: 'انصراف',
  /* Row names down the left of the month strip. */
  Day: 'اليوم',
  Site: 'الموقع',
  State: 'الحالة',
  Reason: 'السبب',
  Remarks: 'ملاحظات',
  Who: 'من',
  When: 'متى',
  Why: 'السبب',
  Period: 'الفترة',
  Plan: 'الخطة',
  Column: 'العمود',
  Figure: 'البيان',
  Value: 'القيمة',

  /* ---- the dashboard export ----------------------------------------------
   * Its own rows, which no other document uses. They were the last block of
   * English on an otherwise Arabic sheet: the figures translated, the words
   * beside them did not, and a reader got half a page in each language.
   * -------------------------------------------------------------------- */
  Dashboard: 'لوحة المعلومات',

  /* ---- what each document calls itself -----------------------------------
   * The heading at the top of an exported list. These doubled as file names,
   * so they printed in English on Arabic sheets — see exportTitle in
   * DataTable. The file names stay Latin; these are what is read.
   * -------------------------------------------------------------------- */
  Employees: 'الموظفون',
  Projects: 'المشاريع',
  Advances: 'السلف',
  Vehicles: 'المركبات',
  Devices: 'الأجهزة',
  Users: 'المستخدمون',
  Attendance: 'الحضور',
  Payslip: 'قسيمة الراتب',
  /* What an outside worker's document calls itself. Missing until now, so
     the one document in the system whose title is the whole point of it
     — a record of work done and money paid, not a payslip — printed that
     title in English on an Arabic page. The guards did not see it because
     the title reaches docLabel through titleFor() rather than as a
     literal; see anOutsideWorkerCarriesNoCompany.test.ts. */
  'Work Voucher': 'قسيمة عمل',
  'Job cost': 'تكلفة المشاريع',
  'Activity log': 'سجل النشاط',
  'Email log': 'سجل البريد',
  'Sign-in log': 'سجل تسجيل الدخول',

  /* ---- values the database stores as codes -------------------------------
   * A leave type and a vehicle kind are stored as an upper-case code and were
   * printed as one. "ANNUAL" in an Arabic column is not a word anybody reads.
   * -------------------------------------------------------------------- */
  ANNUAL: 'سنوية',
  MEDICAL: 'مرضية',
  SICK: 'مرضية',
  UNPAID: 'بدون أجر',
  MATERNITY: 'أمومة',
  HAJJ: 'حج',
  EMERGENCY: 'طارئة',
  bus: 'حافلة',
  car: 'سيارة',
  pickup: 'بيك أب',
  truck: 'شاحنة',
  van: 'فان',
  yes: 'نعم',
  no: 'لا',
  'all sites': 'كل المواقع',
  'On the books': 'على رأس العمل',
  'Attendance rate': 'نسبة الحضور',
  'Absence rate': 'نسبة الغياب',
  'Overtime hours': 'ساعات العمل الإضافي',
  'Active sites': 'المواقع النشطة',
  'Advances outstanding': 'السلف المستحقة',
  'People those absences belong to': 'عدد الأشخاص أصحاب هذه الغيابات',
  'Days present': 'أيام الحضور',
  'Days absent': 'أيام الغياب',
  'Days on leave': 'أيام الإجازة',
  Fields: 'الحقول',
  Size: 'الحجم',
  Build: 'الإصدار',
  Password: 'كلمة المرور',
  'Sign in with': 'تسجيل الدخول بـ',
  'Linked employee': 'الموظف المرتبط',
  'Assigned to': 'مُسند إلى',
  'Attached by': 'أرفقه',
  'Marked by hand': 'مُسجّل يدوياً',
  'Must be filled': 'حقل مطلوب',
  'What goes in it': 'المحتوى',

  // ---- days and hours -----------------------------------------------------
  Date: 'التاريخ',
  Dates: 'التواريخ',
  Days: 'الأيام',
  'Days paid': 'الأيام المدفوعة',
  'Paid days': 'الأيام المدفوعة',
  /* Not "Man-days". It is the trade's word and it names half the workforce
     out of a figure that counts all of it. The Arabic was already neutral. */
  'Person-days': 'أيام العمل',
  Worked: 'أيام عمل فعلية',
  Absent: 'أيام غياب',
  Leave: 'أيام إجازة',
  Hours: 'الساعات',
  Normal: 'عادي',
  'Normal hrs': 'الساعات العادية',
  OT: 'إضافي',
  'OT hrs': 'ساعات إضافية',
  'Approved OT': 'إضافي معتمد',
  Overtime: 'العمل الإضافي',
  'In / Out': 'الدخول / الخروج',
  Started: 'البداية',
  /* The project report's own pair. 'Ends' had no entry at all and printed
     in English on an Arabic page; 'Running' was a second copy of the Arabic
     written inline at the call site, where nothing kept the two in step. */
  Ends: 'النهاية',
  Running: 'مدة التشغيل',
  'Ended at job': 'انتهى في المشروع',
  Idle: 'التوقف',
  Stops: 'التوقفات',
  Distance: 'المسافة',
  Driving: 'القيادة',
  'Last seen': 'آخر ظهور',
  Battery: 'البطارية',
  Boundary: 'النطاق',
  km: 'كم',
  'Time left': 'المتبقي',
  'Expected back': 'العودة المتوقعة',
  /* The staff file's column: the date service ended. "Time left" and "Loan
     left" below carry the other sense of the word, which is why this one is
     spelled out rather than left to a single word that means both. */
  Left: 'تاريخ المغادرة',
  Expires: 'ينتهي في',
  Expiry: 'تاريخ الانتهاء',
  'Passport expiry': 'انتهاء الجواز',
  'Visa expiry': 'انتهاء الإقامة',
  Issued: 'تاريخ الإصدار',
  Joined: 'تاريخ الالتحاق',
  /* The pair either side of a date range — a gratuity band's, a leave
     cycle's. `To` was here and `From` was not, because every range printed so
     far had put a word other than "from" over its opening date. */
  /* The attendance import: the sheet the office fills in and hands back. The
     four day headings and the two identifying columns stay in English on the
     grid itself — the parser finds its columns by them — and everything that
     explains them is translated. */
  'How to fill this in': 'كيف تُملأ',
  Refused: 'مرفوض',
  'rest day': 'راحة أسبوعية',
  From: 'من',
  To: 'إلى',
  /* The attendance sheet's two time columns. They were one cell reading
     "07:00 – 17:30"; the office reads the paper sheet across in a fixed order
     and expects two. */
  Start: 'الدخول',
  End: 'الخروج',
  /* The job number, under the word the office uses for it out loud. */
  'Site no.': 'رقم الموقع',
  Did: 'الإجراء',

  // ---- money --------------------------------------------------------------
  Amount: 'المبلغ',
  Salary: 'الراتب',
  Basic: 'الأساسي',
  Gross: 'إجمالي الأجر',
  Earned: 'المستحق',
  Additions: 'الإضافات',
  Deductions: 'الاستقطاعات',
  'Net payable': 'صافي المستحق',
  'Paid already': 'المدفوع مسبقاً',
  Advance: 'السلفة',
  Instalments: 'الأقساط',
  Repaid: 'المسدد',
  'Loan left': 'رصيد السلفة',
  /* Not المستحق, which is what Earned above already says. A ledger that
     prints both columns printed the same Arabic word over each, and the two are
     opposites: one is what he has earned, the other what he still owes. */
  Outstanding: 'المتبقي',
  Budget: 'الميزانية',
  Allocated: 'المخصص',
  'Labour cost': 'تكلفة الأيدي العاملة',
  Trade: 'المهنة',
  /* The office somebody is on the books of — the column on the roster and
     the heading over each block of a payroll document. See 0198. */
  Branch: 'الفرع',
  'Main company': 'الشركة الرئيسية',
  'No branch set': 'بدون فرع',
  'All branches together': 'إجمالي كل الفروع',
  People: 'الأشخاص',
  'Not in a group': 'خارج المجموعات',
  'What each trade cost': 'تكلفة كل مهنة',
  /* The second block of the same breakdown, on its own page. The trade a
     company's own crew works in and the trade it hired for three weeks have
     one name and are not one figure. */
  'What each trade cost — outside labour':
    'تكلفة كل مهنة — عمالة من خارج الشركة',
  'Cost by trade': 'التكلفة حسب المهنة',
  /* What a trade was allowed to cost on a site, beside what it has taken.
     The share is on the row because the colour on screen has to have a
     visible reason — and on paper there is no colour at all. */
  'Budget by group': 'الميزانية حسب المجموعة',
  Group: 'المجموعة',
  Spent: 'المنفق',
  /* Not Left, which this table already uses for the date somebody left the
     company. A column headed with one word meaning two things is how a
     leaving date ends up over a column of money. */
  'Budget left': 'المتبقي من الميزانية',
  Used: 'المستخدم',
  'Warn at': 'تنبيه عند',
  'Over budget': 'تجاوز الميزانية',
  /* The hours the site itself keeps, which is what its crew's check-in is
     marked against. See migration 0197. */
  'Duty hours': 'ساعات الدوام',
  'Company rule': 'قاعدة الشركة',
  'Normal cost': 'التكلفة العادية',
  'OT cost': 'تكلفة العمل الإضافي',
  'OT pay': 'أجر العمل الإضافي',
  'Payroll cost': 'تكلفة الرواتب',
  'Job no.': 'رقم المشروع',
  Total: 'الإجمالي',

  // ---- the payslip's own words -------------------------------------------
  // Its identity block is a fixed list of the papers a UAE employer holds, and
  // the sheet is often produced at a bank, a clinic or a gate — which is
  // exactly where the Arabic version is the one being asked for.
  EMPLOYEE: 'بيانات الموظف',
  'THIS MONTH': 'هذا الشهر',
  /* The payslip's four section bands. They go to sectionBand as bare literals,
     which translates them through docWord — so nothing collected them and every
     Arabic payslip this system has produced carried four English headings down
     the middle of it. */
  '1 · Employee': '1 · الموظف',
  '2 · The month': '2 · الشهر',
  '3 · Days and overtime by job number': '3 · الأيام والعمل الإضافي حسب رقم المشروع',
  '4 · Pay': '4 · الأجر',
  'OUTSIDE WORKER JOB COST BREAKDOWN': 'تفصيل تكلفة العمالة من خارج الشركة',
  /* The two blocks a payroll sheet is made of, and the rows that close them.
     They reach the sheet as bare literals through docWord, so an Arabic
     workbook carried two English headings down the middle of it until these
     were collected. */
  'COMPANY PAYROLL': 'رواتب الشركة',
  'OUTSIDE WORKER EXPENSES': 'مصروفات العمالة من خارج الشركة',
  'Company payroll': 'رواتب الشركة',
  'Outside labour': 'عمالة من خارج الشركة',
  'Payroll and outside labour together': 'الرواتب والعمالة من خارج الشركة معاً',
  'SITES THIS MONTH': 'مواقع العمل هذا الشهر',
  'OVERTIME BY DAY': 'الوقت الإضافي حسب اليوم',
  /* Was 'WHERE IT WENT'. Renamed by the office that reads it: the two bars
     are an allocation of one figure, and 'where it went' reads as a question
     about money that has gone missing. */
  'AMOUNT ALLOCATION': 'توزيع المبلغ',
  // The day-count beside Worked, Leave and Absent, not the leave type.
  'Holiday': 'عطلة',
  'Monthly salary': 'الراتب الشهري',
  /* The same column, said correctly. basic is a month for a monthly
     worker and one day for a daily one — see rateLabel. */
  'Daily rate': 'الأجر اليومي',
  Mobile: 'رقم الهاتف',
  'Second number': 'رقم آخر',
  Email: 'البريد الإلكتروني',
  'Passport no.': 'رقم الجواز',
  'Passport issued': 'تاريخ إصدار الجواز',
  'Passport expires': 'تاريخ انتهاء الجواز',
  'Labour card': 'بطاقة العمل',
  'Labour card expires': 'انتهاء بطاقة العمل',
  'Visa entry': 'تاريخ دخول الإقامة',
  'Visa expires': 'انتهاء الإقامة',
  /* The Emirates ID's own expiry, which is the residence visa's — the two
     dates are one date, printed on the card. The payslip says 'ID expiry'
     because that is the card in somebody's pocket; the column behind it is
     still visa_expiry. */
  'ID expiry': 'انتهاء الهوية',
  'Last leave': 'آخر إجازة',
  Returned: 'تاريخ العودة',
  'Gross earning': 'إجمالي الأجر',
  'Pre-payment': 'دفعة مقدمة',
  'Previous Leave Salary': 'بدل إجازة سابقة',
  /* Money already handed over against a settlement, and how it reached
     them. Codes are stored; these are what a person reads. See 0174.

     The line itself is 'Pre-payment', above — it was 'Paid in advance' and the
     office asked for the one word it already uses on the adjustments screen, so
     the same movement of money is called the same thing wherever it appears. */
  'bank transfer': 'حوالة بنكية',
  'salary card': 'بطاقة الراتب',
  cheque: 'شيك',
  card: 'بطاقة',

  /* Added and taken off by hand on a settlement — see SettlementDialog. */
  Bonus: 'مكافأة',
  /* The company's own float coming back, which is not a bonus and must not
     read as one on a document anybody reconciles against a cash book. */
  'Petty Cash': 'عهدة نقدية',
  'Outside workers': 'عمال من خارج الشركة',
  /* A settlement that is neither the annual cycle nor the end of
     employment. See 0171. */
  emergency: 'طارئة',
  /* A settlement against sick leave. See 0173. */
  medical: 'مرضية',
  /* The other two values employee_leave_cycles.leave_type holds. All four reach
     docWord as data rather than as a literal, so no guard collects them — a
     missing one prints an English word in an Arabic column and says nothing.
     These two had no entry until the leave log started printing the type. */
  annual: 'سنوية',
  unpaid: 'بدون راتب',
  'of which worked': 'منها أيام عمل فعلية',
  'All amounts in': 'جميع المبالغ بـ',
  'NET SALARY FOR': 'صافي الراتب عن',
  'Advance still outstanding': 'رصيد السلفة المتبقي',
  /* 0159. "Already paid" read as though the wage had been paid; this is
     money handed over before pay day and taken off it. */
  'Pre-paid': 'دفعة مقدمة',
  'Outstanding advance': 'رصيد السلفة',
  holiday: 'عطلة',
  leave: 'إجازة',
  days: 'أيام',
  /* Duration words, for the spans printed beside the dates in the identity
     block — how long somebody has served, how long a document has left. */
  day: 'يوم',
  month: 'شهر',
  months: 'أشهر',
  year: 'سنة',
  years: 'سنوات',
  Service: 'الخدمة',
  Valid: 'سارٍ',
  Expired: 'منتهٍ',
  Over: 'مضى',
  hrs: 'ساعات',

  // ---- the settlement -----------------------------------------------------
  'FINAL SETTLEMENT': 'التسوية النهائية',
  'ANNUAL LEAVE STATEMENT': 'كشف الإجازة السنوية',
  'Prepared by': 'أعدّه',
  /* ---- the import catalogue ---------------------------------------------
     The names of the things a spreadsheet column can be pointed at. They are
     the whole of the mapping screen — the list somebody reads while deciding
     what their own column means — and they reach the import report as its
     headings too. Held here rather than beside each field, because the same
     words are already column headings on half the documents in this system.
     -------------------------------------------------------------------- */
  'Basic salary': 'الراتب الأساسي',
  /* ---- the platform console ----------------------------------------------
     Column headings and the values a registration can hold. The two coverage
     tests found these the moment they were written, which is the whole reason
     those tests read the call sites rather than a list. */
  'Company': 'الشركة',
  'Currency': 'العملة',
  'Logins': 'حسابات الدخول',
  'Actions': 'إجراءات',
  'trade_licence': 'الرخصة التجارية',
  'establishment_card': 'بطاقة المنشأة',
  'vat_certificate': 'شهادة ضريبة القيمة المضافة',
  'more_info': 'بانتظار إيضاح',
  'leave salary': 'بدل الإجازة',
  'air ticket': 'تذكرة السفر',
  'one way out': 'ذهاب فقط',
  'return flight': 'رحلة عودة',
  'return ticket': 'تذكرة ذهاب وعودة',
  'measured over': 'محسوبة على مدى',
  /* ---- the date and time shapes a company chooses -----------------------
     Read on the settings screen while somebody decides how this company
     writes a date. The example beside each one carries the real meaning; the
     name is what they scan down.
     -------------------------------------------------------------------- */
  'Day, short month, year': 'اليوم، الشهر مختصراً، السنة',
  'Day first, slashes': 'اليوم أولاً، بشرطات مائلة',
  'Month first, slashes': 'الشهر أولاً، بشرطات مائلة',
  'Day first, dashes': 'اليوم أولاً، بشرطات',
  'Year first': 'السنة أولاً',
  'Day, short month, short year': 'اليوم، الشهر مختصراً، السنة مختصرة',
  'Short month, day, year': 'الشهر مختصراً، اليوم، السنة',
  'Day, full month, year': 'اليوم، الشهر كاملاً، السنة',
  'Full month, day, year': 'الشهر كاملاً، اليوم، السنة',
  'Ordinal day, full month, year': 'اليوم بالترتيب، الشهر كاملاً، السنة',
  'Twenty-four hour': 'أربع وعشرون ساعة',
  'Twelve hour, am and pm': 'اثنتا عشرة ساعة، صباحاً ومساءً',

  /* ---- the sections of the company rules --------------------------------- */
  'Working day': 'يوم العمل',
  'Salary & rates': 'الرواتب والأجور',
  'Payroll run': 'دورة الرواتب',
  /* تحديد الموقع, which is what the phone is doing. كشف الموقع reads as a
     statement about the site, because كشف is the word this same file uses for
     كشف الرواتب and كشف الإجازة السنوية. */
  'Site detection': 'تحديد الموقع',
  'Absence alerts': 'تنبيهات الغياب',
  'Leave types': 'أنواع الإجازات',
  'Public holidays': 'العطل الرسمية',
  'Company rules': 'قواعد الشركة',

  /* ---- the self check and the notification test -------------------------- */
  'Phone clock against the server': 'ساعة الهاتف مقابل الخادم',
  'Location here, now': 'الموقع هنا، الآن',
  'Camera': 'الكاميرا',
  'Face recognition models': 'نماذج التعرف على الوجه',
  'Connection to the database': 'الاتصال بقاعدة البيانات',
  'Waiting to be sent': 'في انتظار الإرسال',
  'Running on': 'يعمل على',
  'Stopped before it was raised': 'أُوقف قبل إطلاقه',
  'Raised': 'أُطلق',
  'In the bell': 'في الجرس',
  'Not sent to the bell': 'لم يُرسل إلى الجرس',
  'No phone alert attempted': 'لم تُحاوَل أي تنبيهات على الهاتف',
  'The phone was left alone': 'تُرك الهاتف دون إزعاج',
  'The sender could not be reached': 'تعذّر الوصول إلى المُرسِل',
  'The sender ran': 'عمل المُرسِل',
  'Firebase accepted it': 'قبله Firebase',
  'Firebase refused it': 'رفضه Firebase',
  'Still sitting in the queue': 'ما زال في الطابور',

  /* ---- documents a fleet or a project keeps ------------------------------ */
  'Accident report': 'تقرير حادث',
  'Building permit': 'رخصة بناء',
  'Client or consultant approval': 'موافقة العميل أو الاستشاري',
  'Contract or LPO': 'عقد أو أمر شراء',
  'Drawing': 'مخطط',
  'Fine': 'مخالفة',
  'Insurance certificate': 'شهادة تأمين',
  'Police report': 'تقرير شرطة',
  'Registration card (Mulkiya)': 'بطاقة التسجيل (الملكية)',
  'Other': 'أخرى',

  /* ---- the settlement's own lines, and a payroll column or two ----------- */
  'Salaries Calculation': 'احتساب الرواتب',
  'Leave Overstayed': 'تجاوز مدة الإجازة',
  'Advance Charged To End-of-service': 'سلفة محمّلة على نهاية الخدمة',
  'Advance Outstanding': 'سلفة قائمة',
  /* What the unpaid months' own payslips were going to take off the advance
     — one line, the months in its note. See computeSettlement. */
  'Advance Recovery': 'سداد سلفة',
  'Loan balance': 'رصيد القرض',
  'Salary for days payable': 'راتب الأيام المستحقة',
  'Total salary': 'إجمالي الراتب',
  'Gross salary': 'الراتب الإجمالي',
  'Net salary': 'صافي الراتب',
  'OT hours': 'ساعات العمل الإضافي',
  'OT salary': 'أجر العمل الإضافي',
  'Overtime — night': 'عمل إضافي — ليلي',
  'Overtime — weekly holiday': 'عمل إضافي — عطلة أسبوعية',
  /* The label 0156 replaced it with. The old one stays: payslip_lines rows
     written before that still carry it, and a document has to keep reading in
     Arabic years after the wording behind it changed. */
  'Overtime — weekly rest day': 'عمل إضافي — راحة أسبوعية',
  'Overtime — public holiday': 'عمل إضافي — عطلة رسمية',
  'Employee name': 'اسم الموظف',
  'Job title': 'المسمى الوظيفي',
  'Sr.': 'م.',
  /* The same column under its other name. Two spellings because two documents
     chose differently and neither is worth changing on a form somebody's
     accounts department already reads. */
  'No.': 'م.',
  /* In DOC_STRINGS as `employeeId` and not here — and this is the table
     docWord reads, so the salary summary printed the English heading over an
     Arabic column. A keyed entry does not reach a heading passed as a bare
     literal to headerRow. */
  'Employee ID': 'رقم الموظف',
  /* And the capitalised spelling, which is the one on the attendance import
     template. Only 'Employee name' was here, so the notes page explaining
     that column carried an English heading over an Arabic explanation. */
  'Employee Name': 'اسم الموظف',
  /* Which salary line the row came off, where somebody has more than one. */
  Line: 'البند',
  'Row order': 'ترتيب الصفوف',
  'Extra columns': 'أعمدة إضافية',

  /* ---- what the import does with a row that is already there ------------- */
  'Add and update': 'إضافة وتحديث',
  'Add new only': 'إضافة الجديد فقط',
  'Update existing only': 'تحديث الموجود فقط',
  'Add': 'إضافة',
  'Deduct': 'خصم',

  /* ---- the places this app can take somebody ----------------------------- */
  'Appearance': 'المظهر',
  'Fleet': 'الأسطول',
  'Leaves': 'الإجازات',
  'Loans & Advances': 'القروض والسلف',
  'Monthly grid': 'الشبكة الشهرية',
  'Payroll': 'الرواتب',
  'Reports': 'التقارير',
  /* نداء الأسماء, which is what the stored `rollcall` value already says.
     النداء on its own is just "the call". */
  'Roll-call': 'نداء الأسماء',
  'Users & logins': 'المستخدمون وتسجيل الدخول',
  'Emirates ID': 'الهوية الإماراتية',
  'Bank': 'البنك',
  'Notes': 'ملاحظات',
  'Location': 'الموقع',
  'Code': 'الرمز',
  'Category': 'الفئة',
  /* ---- the payslip, laid out as the office's own sheet ------------------
     Its sections, its two new money columns, and the two cards that show
     what the gross is made of and what became of it. See payslipPdf. */
  'SITE SUMMARY': 'ملخص المواقع',
  /* Set like the other two section heads rather than in sentence case —
     'Daily attendance' is also the name of a report, and that one stays
     as it is written on its own button. */
  'DAILY ATTENDANCE': 'الحضور اليومي',
  'EARNINGS & DEDUCTIONS': 'المستحقات والاستقطاعات',
  /* 'Daily attendance' is already here, under the reports — it is the name of
     one of them, and the payslip's own attendance heading is the same two
     words. */
  'Day amount': 'مبلغ الأيام',
  'OT amount': 'مبلغ الإضافي',
  'Gross earning breakdown': 'تفصيل إجمالي المستحقات',
  'Where it went': 'أين ذهب',
  /* The second of the two breakdown cards, named for the three figures
     in it rather than for the question they answer — which is how the
     office's own sheet names it. */
  'Net salary, deductions & pre-payment': 'صافي الراتب والاستقطاعات والدفعة المقدمة',
  /* A rest day is a day he was not expected, which is not a day he was
     absent — the band counts them apart for that reason. */
  'Rest': 'راحة',
  /* On a leave that has no return date against it yet. */
  'still away': 'ما زال خارج العمل',
  'Housing allowance': 'بدل السكن',
  'Transport allowance': 'بدل المواصلات',
  'Food allowance': 'بدل الطعام',
  'Other allowance': 'بدل آخر',
  'Overtime rate per hour': 'أجر ساعة العمل الإضافي',
  'Employee number': 'رقم الموظف',
  'Full name': 'الاسم الكامل',
  'Name in Arabic': 'الاسم بالعربية',
  'Joining date': 'تاريخ الالتحاق',
  'Crew': 'الفريق',
  'Staff file': 'ملف الموظفين',
  'Changed by hand': 'عُدّل يدوياً',
  'Marked off site': 'سُجّل خارج الموقع',
  /* Its opposite, new with 0221: the strip marks both ends of a scanned day
     now, so the key has to name the good one too. */
  'Scanned on site': 'مُسجّل داخل الموقع',

  'Employment type': 'نوع التوظيف',
  'Probation ends': 'تنتهي فترة التجربة',
  'Left on': 'تاريخ ترك العمل',
  'Reason for leaving': 'سبب ترك العمل',
  'Labour card number': 'رقم بطاقة العمل',
  'Passport number': 'رقم جواز السفر',
  /* The word on its own. The payslip's identity card pairs it with the expiry
     in a chip beside it, so the label is one word and not "Passport no." — and
     with no entry of its own it printed "Passport:" in the middle of an Arabic
     card. */
  'Passport': 'جواز السفر',
  'Date of birth': 'تاريخ الميلاد',
  'Home phone': 'هاتف المنزل',
  'Work phone': 'هاتف العمل',
  'Home address': 'عنوان المنزل',
  'Address in the UAE': 'العنوان في الإمارات',
  'Emergency contact': 'جهة الاتصال للطوارئ',
  'Emergency phone': 'هاتف الطوارئ',
  'Routing code': 'رمز التوجيه المصرفي',
  'Projects and sites': 'المشاريع والمواقع',
  'Job number': 'رقم المشروع',
  'Project name': 'اسم المشروع',
  'Start date': 'تاريخ البدء',
  'End date': 'تاريخ الانتهاء',
  'Project manager': 'مدير المشروع',
  'Latitude': 'خط العرض',
  'Longitude': 'خط الطول',
  'Boundary radius (m)': 'نصف قطر النطاق (م)',
  /* The rest of a site's own row, added when the site list became a file a
     company can keep and put back — see theProjectFile. Every one of them is
     a heading in the projects import template as well, which reads its words
     from the same field list. */
  'Site day starts': 'يبدأ دوام الموقع',
  'Site day ends': 'ينتهي دوام الموقع',
  'Late grace (minutes)': 'سماح التأخير (دقائق)',
  'Budget alert at (%)': 'تنبيه الميزانية عند (%)',
  'Arabic name': 'الاسم بالعربية',
  'Boundary name': 'اسم النطاق',
  Boundaries: 'النطاقات',
  'Site boundaries': 'نطاقات المواقع',
  /* The daily attendance email — see theDayInOneEmail. One a company, every
     evening, in whichever language that company reads. */
  'On site': 'في الموقع',
  'Rest day or holiday': 'راحة أو عطلة',
  'Not marked': 'لم يُسجل',
  'Not joined': 'لم يلتحق بعد',
  'Designations': 'المسميات الوظيفية',
  'Salary revisions': 'تعديلات الرواتب',
  /* The advance and leave files, which are the same document read in two
     directions: every column the importer accepts, filled in. See 0213. */
  'Date given': 'تاريخ الصرف',
  'Instalment amount': 'قيمة القسط',
  'Recovery starts': 'يبدأ الاستقطاع',
  Recovered: 'المستقطع',
  Balance: 'الرصيد',
  'Leave records': 'سجلات الإجازات',
  'leave records': 'سجلات الإجازات',
  'Half day': 'نصف يوم',
  'Actually back': 'عاد فعلياً',
  'Effective from': 'ساري اعتباراً من',
  'salary records': 'سجلات الرواتب',
  'designations': 'المسميات الوظيفية',
  'projects': 'المشاريع',
  'employees': 'الموظفون',
  'Approved by': 'اعتمده',
  'Signature of the employee': 'توقيع الموظف',
  /* The three lines a settlement is signed on, in the order they are signed:
     prepared, checked, acknowledged. They reach docWord from inside
     `signatures`, so no guard collects them — a missing entry here is an
     English line under an Arabic document and nothing says so. */
  /* Over the settlement's reference, at the head of the page. */
  Reference: 'الرقم المرجعي',
  'Prepared by (HR / Payroll)': 'أعدّه (الموارد البشرية / الرواتب)',
  'Checked by (Finance)': 'راجعه (المالية)',
  'Employee acknowledgment': 'إقرار الموظف',
  'Received by (employee)': 'استلمه (الموظف)',
  'Overtime rate': 'سعر العمل الإضافي',
  'Ticket': 'التذكرة',
  /* A line of the settlement's earnings, and the kind a ticket is given when
     nobody typed one. Both printed in English on an Arabic settlement: the
     first in the totals, the second in "التذكرة (Air ticket)". */
  'Gratuity': 'مكافأة نهاية الخدمة',
  'Air ticket': 'تذكرة طيران',
  'one way': 'ذهاب فقط',
  'Final Settlement': 'التسوية النهائية',
  'Annual Leave Statement': 'كشف الإجازة السنوية',
  'basic': 'الأساسي',
  'gross': 'الإجمالي',
  'Checked by': 'راجعه',
  'Received by': 'استلمه',
  Signature: 'التوقيع',
  /* ---- the payment voucher sheet, and the rejection file beside it -------
     Printed for signatures and handed round, so every heading on it is read by
     somebody who may have no English at all. See Payments.tsx. */
  'Payment vouchers': 'سندات الدفع',
  /* The credit at the foot of every PDF. Only these two words are translated:
     "NoorOne" is a product name and is written the same in both. */
  'Powered by': 'مدعوم بواسطة',
  /* ---- the ribbon in the corner of a printed document -------------------
     Short on purpose: it is set in eleven points of band and has to read at a
     glance from a fanned stack. See statusRibbon. */
  'DRAFT': 'مسودة',
  'APPROVED': 'معتمد',
  'PART PAID': 'مدفوع جزئياً',
  'PAID': 'مدفوع',
  'Voucher': 'سند',
  'Paid on': 'تاريخ الدفع',
  'Method': 'طريقة الدفع',
  'ROW': 'السطر',
  'VALUE': 'القيمة',
  'WHY': 'السبب',
  'End of service': 'نهاية الخدمة',
  /* بدل الإخطار, not بدل الإنذار. The UAE labour law calls the notice
     period الإخطار; إنذار is a disciplinary warning, which is what
     warning_letter above means by it. Payment in lieu of notice on a
     settlement read as a payment in lieu of being told off. */
  'Notice pay': 'بدل الإخطار',
  'Service years': 'سنوات الخدمة',

  /* ---- the settlement's own headings --------------------------------------
   * Every one of these was already being printed on the Arabic settlement, in
   * English, and had been since the workbook was written.
   *
   * They arrived here late because of where they are written. A heading passed
   * to `headerRow` or `sectionBand` is a bare literal inside an array — the
   * function translates it through docWord, but the guard in
   * everyLabelTranslated collects `docLabel('…')` and `t('…')` call sites and
   * cannot see an array of strings. So the fallback did what it is designed to
   * do: printed the English rather than throwing, on the one document somebody
   * signs their name to, silently, for as long as the module has existed.
   *
   * The settlement PDF calls docLabel on the same literals, which is what made
   * them visible — and adding them fixes the workbook at the same time, since
   * both documents now read one table.
   * --------------------------------------------------------------------- */
  '1 · Salary still owed': '1 · الرواتب المستحقة غير المدفوعة',
  '2 · Service and leave': '2 · الخدمة والإجازات',
  '3 · Gratuity': '3 · مكافأة نهاية الخدمة',
  'Total salary owed': 'إجمالي الرواتب المستحقة',
  'Month': 'الشهر',
  'Month total': 'إجمالي الشهر',
  /* The wage in its two halves, as the payslip shows it. 'الأساسي' rather than
     'الراتب الأساسي' — that spelling is already the info block's Basic salary,
     and a column heading beside Allowances does not need the noun twice. */
  'Basic pay': 'الأساسي',
  Allowances: 'البدلات',
  /* Beside the day count on a gratuity band, never instead of it. */
  Years: 'السنوات',
  /* The "Additional" column: a site allowance, watchman duty, whatever it was.
     Two headings because the sheet carries the reason and the figure apart —
     a figure with no reason beside it is the line that gets queried. */
  'Extra for': 'الإضافي عن',
  'Extra': 'مبلغ إضافي',

  'Joined on': 'تاريخ الالتحاق',
  'Last working day': 'آخر يوم عمل',
  'Total service': 'إجمالي مدة الخدمة',
  'Approved leave': 'الإجازة المعتمدة',
  'Went on leave': 'تاريخ بدء الإجازة',
  'Worked for': 'مدة العمل',
  'Days taken': 'الأيام المستخدمة',
  'Days worked': 'أيام العمل',
  'Departure': 'تاريخ المغادرة',
  'Leave Salary': 'راتب الإجازة',
  'This cycle — joined': 'هذه الدورة — تاريخ الالتحاق',
  /* The number nobody ever added up: days returned beyond what was approved,
     cycle by cycle, and then as a total at the foot of the column. */
  'Over by': 'أيام التجاوز',
  'days over in total': 'يوماً تجاوزاً في المجموع',

  'Band': 'الشريحة',
  'Days per year': 'أيام عن كل سنة',
  'Gratuity days': 'أيام المكافأة',

  /* المستحقات rather than المستحق له. The English says "owed to them" and the
     Arabic would have to pick a gender to say the same thing; the plural noun
     says it about the money instead, which is whose column it is. */
  'Owed to them': 'المستحقات',
  'Pre-paid / deducted': 'مدفوع مسبقاً / مخصوم',

  /* The band across the foot of a settlement, which says which way the money
     goes. Both were reworded in English — "paying now" became "payable" —
     and neither had an Arabic entry until a guard followed the local `t` into
     this file and found them. On an Arabic settlement the one line that says
     who owes whom was printing in English. */
  'Payable to the employee': 'المستحق للموظف',
  'Owed by the employee': 'المستحق على الموظف',
  /* In the returned-on column of a leave cycle: he has not come back. */
  'N/A': 'لا ينطبق',
  'Total owed': 'إجمالي المستحقات',
  'Total paid': 'إجمالي المدفوع',

  'Leave type': 'نوع الإجازة',
  'Last rejoining': 'تاريخ العودة الأخيرة',
  'Leave day rate': 'أجر يوم الإجازة',
  /* The ticket ledger. `Issued` is on the passport block above, and the three
     flight directions are with the other leave-cycle words. */
  Flight: 'الرحلة',
  'Running total': 'الإجمالي التراكمي',

  /* ---- the words this system puts in a cell -------------------------------
   * A report's headings were the easy half. The cells under them hold system
   * vocabulary too — a status, a kind, a state — and a document whose headings
   * are Arabic over a column of "active" is the half-translated page this file
   * exists to prevent.
   *
   * Both spellings of each: the raw value as the database stores it, and the
   * form a screen shows. Different exports carry different ones and neither is
   * worth normalising at forty call sites. */
  active: 'نشط',
  Active: 'نشط',
  inactive: 'موقوف',
  Inactive: 'موقوف',
  'Inactive — stood down': 'موقوف عن العمل',
  on_leave: 'في إجازة',
  'On leave': 'في إجازة',
  suspended: 'موقوف مؤقتاً',
  Suspended: 'موقوف مؤقتاً',
  /* 'انتهت خدمته' says "his service ended". The status belongs to a person
     whose gender this system does not record and has no business guessing. */
  left: 'انتهت الخدمة',
  planned: 'مخطط',
  Planned: 'مخطط',
  completed: 'منجز',
  Completed: 'منجز',
  on_hold: 'متوقف',
  'On hold': 'متوقف',
  present: 'حاضر',
  Present: 'حاضر',
  rest_day: 'يوم راحة',
  'Rest day': 'يوم راحة',
  'Not yet marked': 'لم يُسجّل بعد',
  Pending: 'قيد الانتظار',
  Approved: 'معتمد',
  Rejected: 'مرفوض',
  Open: 'مفتوح',
  Closed: 'مغلق',
  Paid: 'مدفوع',
  Unpaid: 'غير مدفوع',
  Yes: 'نعم',
  No: 'لا',
  Male: 'ذكر',
  Female: 'أنثى',

  /* ---- nationalities -----------------------------------------------------
   * Typed into a free field, so this is a best effort rather than a closed
   * list: the ones a UAE site office actually types, in both the country and
   * the demonym form, because both get entered. Anything else prints as typed,
   * which is the correct failure — a guessed transliteration of a nationality
   * on a document somebody produces at a bank is worse than the English. */
  Pakistani: 'باكستاني',
  Pakistan: 'باكستان',
  Indian: 'هندي',
  India: 'الهند',
  Bangladeshi: 'بنغلاديشي',
  Bangladesh: 'بنغلاديش',
  'Sri Lankan': 'سريلانكي',
  'Sri Lanka': 'سريلانكا',
  Nepali: 'نيبالي',
  Nepal: 'نيبال',
  Filipino: 'فلبيني',
  Philippines: 'الفلبين',
  Egyptian: 'مصري',
  Egypt: 'مصر',
  Sudanese: 'سوداني',
  Sudan: 'السودان',
  Syrian: 'سوري',
  Syria: 'سوريا',
  Jordanian: 'أردني',
  Jordan: 'الأردن',
  Lebanese: 'لبناني',
  Lebanon: 'لبنان',
  Yemeni: 'يمني',
  Yemen: 'اليمن',
  Afghan: 'أفغاني',
  Afghanistan: 'أفغانستان',
  Emirati: 'إماراتي',
  'United Arab Emirates': 'الإمارات العربية المتحدة',
  Iranian: 'إيراني',
  Iran: 'إيران',
  Nigerian: 'نيجيري',
  Nigeria: 'نيجيريا',
  Kenyan: 'كيني',
  Kenya: 'كينيا',
  Ugandan: 'أوغندي',
  Uganda: 'أوغندا',
  Ethiopian: 'إثيوبي',
  Ethiopia: 'إثيوبيا',

  /* ---- job titles --------------------------------------------------------
   * These come from a table the company fills in itself, so this cannot be
   * complete and is not meant to be. It covers the trades and offices a
   * contracting company in this country actually staffs; a title nobody here
   * anticipated prints as it was typed. */
  Labourer: 'عامل',
  Helper: 'مساعد',
  Mason: 'بنّاء',
  Carpenter: 'نجار',
  Steelfixer: 'حداد تسليح',
  'Steel fixer': 'حداد تسليح',
  Welder: 'لحّام',
  Painter: 'دهّان',
  Plumber: 'سبّاك',
  Electrician: 'كهربائي',
  'AC Technician': 'فني تكييف',
  Technician: 'فني',
  Foreman: 'مشرف عمال',
  Supervisor: 'مشرف',
  'Site Engineer': 'مهندس موقع',
  Engineer: 'مهندس',
  'Project Manager': 'مدير مشروع',
  'Operations Manager': 'مدير العمليات',
  'General Manager': 'المدير العام',
  'Chief Operating Officer (COO)': 'الرئيس التنفيذي للعمليات',
  'Chief Executive Officer (CEO)': 'الرئيس التنفيذي',
  Accountant: 'محاسب',
  'HR Officer': 'مسؤول الموارد البشرية',
  'Safety Officer': 'مسؤول السلامة',
  Storekeeper: 'أمين مخزن',
  Watchman: 'حارس',
  Cleaner: 'عامل نظافة',
  Cook: 'طبّاخ',
  'Heavy Driver': 'سائق ثقيل',
  'Light Driver': 'سائق خفيف',
  'Crane Operator': 'مشغّل رافعة',
  Operator: 'مشغّل',
  Administrator: 'إداري',
  'Office Boy': 'مراسل',

  /* ---- the reports, by the name on their own button ----------------------- */
  'Employee master file': 'ملف بيانات الموظفين',
  'Daily attendance': 'الحضور اليومي',
  'Monthly attendance grid': 'جدول الحضور الشهري',
  'Salary statement': 'كشف الرواتب',
  'Advances ledger': 'سجل السلف',
  'Labour with no job number': 'أيام عمل بلا رقم مشروع',
  Trips: 'الرحلات',
  'Project cost across periods': 'تكلفة المشاريع عبر الفترات',

  /* ---- every word the database itself stores -------------------------------
   * Scraped from the check constraints in supabase/migrations rather than
   * collected by hand, because collecting by hand is what let "absent" through:
   * "present" was translated, "absent" was not, and an attendance export came
   * out with one English word down a column of Arabic ones. Nothing announces
   * that — docLabel returns what it was given, so a missing entry looks exactly
   * like a name it was right to leave alone.
   *
   * docStrings.test.ts now reads those constraints and fails when a value has
   * no entry here, so a status added in a future migration cannot reach a
   * document untranslated.
   *
   * Some words appear in more than one column — 'manual' is a source and a
   * kind, 'other' is half a dozen things — and one entry serves them all. Where
   * two columns would need different Arabic for the same English word, the
   * loader has to translate it itself; none currently do. */
  absent: 'غائب',
  accepted: 'مقبول',
  approved: 'معتمد',
  blocked: 'محظور',
  calculating: 'قيد الاحتساب',
  cancelled: 'ملغى',
  cleared: 'مسوّى',
  closed: 'مغلق',
  confirmed: 'مؤكد',
  disbursed: 'مصروف',
  discarded: 'مُلغى',
  done: 'منجز',
  draft: 'مسودة',
  /* منتهٍ, matching Expired above. It was منتهي here, منتهٍ there and
     منتهية for EXPIRED — three spellings of one word in one document. */
  expired: 'منتهٍ',
  exported: 'مُصدَّر',
  failed: 'فشل',
  in_progress: 'قيد التنفيذ',
  locked: 'مقفل',
  not_joined: 'لم يلتحق',
  open: 'مفتوح',
  paid: 'مدفوع',
  pending: 'قيد الانتظار',
  queued: 'في الانتظار',
  rejected: 'مرفوض',
  pre_restore: 'قبل الاستعادة',
  resolved: 'تمت المعالجة',
  restored: 'تمت الاستعادة',
  retired: 'خارج الخدمة',
  review: 'قيد المراجعة',
  running: 'قيد التشغيل',
  scheduled: 'مجدول',
  sent: 'مُرسل',
  settled: 'مسدد',
  skipped: 'تم تخطيه',
  submitted: 'مقدَّم',
  /* Was 'محل بديل', which is not a phrase — it reads as "a replacement
     premises". A record replaced by a newer one is مستبدَل. */
  superseded: 'مستبدَل',
  taken: 'مستخدمة',
  validated: 'تم التحقق منه',
  withdrawn: 'مسحوب',
  face: 'بصمة الوجه',
  geofence: 'نطاق الموقع',
  manual: 'يدوي',
  /* How a vehicle's card was read — see vehicle_claims.source in 0230. The
     two acronyms stay Latin: they are what is printed on the hardware and on
     the card itself, and an Arabic rendering of "QR" names nothing a driver
     could point at. */
  qr: 'رمز QR',
  nfc: 'بطاقة NFC',
  rollcall: 'نداء الأسماء',
  trip: 'رحلة',
  biometric: 'بصمة',
  password: 'كلمة مرور',
  cash: 'نقداً',
  bank: 'تحويل بنكي',
  payroll: 'من الراتب',
  waiver: 'إعفاء',
  gratuity: 'من مكافأة نهاية الخدمة',
  addition: 'إضافة',
  deduction: 'استقطاع',
  earning: 'استحقاق',
  advance: 'سلفة',
  salary: 'راتب',
  overtime: 'عمل إضافي',
  leave_salary: 'بدل إجازة',
  paid_already: 'مدفوع مسبقاً',
  ticket: 'تذكرة سفر',
  final: 'تسوية نهائية',
  avatar: 'صورة شخصية',
  document: 'مستند',
  payslip: 'قسيمة راتب',
  receipt: 'إيصال',
  vehicle: 'مركبة',
  project: 'مشروع',
  heartbeat: 'إشارة اتصال',
  attendance: 'حضور',
  app: 'التطبيق',
  other: 'أخرى',
  // ---- which way a journey went ----
  outbound: 'ذهاب',
  return: 'عودة',
  both: 'ذهاب وعودة',
  enter: 'دخول',
  exit: 'خروج',
  in: 'دخول',
  out: 'خروج',
  registration: 'ملكية المركبة',
  insurance: 'تأمين',
  fine: 'مخالفة',
  accident_report: 'تقرير حادث',
  police_report: 'تقرير شرطة',
  info: 'معلومة',
  low: 'منخفض',
  normal: 'عادي',
  high: 'مرتفع',
  warning: 'تنبيه',
  critical: 'حرج',
  email: 'بريد إلكتروني',
  push: 'إشعار',
  in_app: 'داخل التطبيق',
  night: 'ليلي',

  // ---- the words the documents register generates --------------------------
  'no expiry': 'بلا تاريخ انتهاء',
  EXPIRED: 'منتهية',
  expiring: 'تقترب من الانتهاء',
  valid: 'سارية',

  /* ---- filled in by the translator ---------------------------------------
     scripts/translate-doc-strings.mjs appends here, and only here. It finds
     the words this application has started printing and has no Arabic for,
     asks a model for each, and writes what a person has approved.

     Move an entry up into the block it belongs to whenever you are next in
     this file. Nothing depends on where a key sits; this is a landing area,
     not a category.
     -------------------------------------------------------------------- */
  // TRANSLATOR ANCHOR — labels. Nothing may be written below this line.
};

/* =============================================================================
 * A length of time, counted the way Arabic counts
 * -----------------------------------------------------------------------------
 * describeGap produces "2 months 3 days" and the documents register puts it in
 * a column. Rewriting that word by word gives "2 أشهر 3 أيام", which is wrong
 * in a way that is obvious to anybody who reads Arabic: the language inflects a
 * counted noun by the number in front of it.
 *
 *     1        يوم        the singular alone
 *     2        يومان      a dual form, which English has no equivalent of
 *     3–10     أيام       the plural
 *     11+      يوماً      back to the singular, in the accusative
 *
 * Four forms, and picking the wrong one reads to an Arabic speaker roughly the
 * way "3 day" reads in English. Worth the twelve lines.
 * ========================================================================== */

const COUNTED = {
  day: { one: 'يوم', two: 'يومان', few: 'أيام', many: 'يوماً' },
  month: { one: 'شهر', two: 'شهران', few: 'أشهر', many: 'شهراً' },
  year: { one: 'سنة', two: 'سنتان', few: 'سنوات', many: 'سنة' },
} as const;

type CountedUnit = keyof typeof COUNTED;

export function docDuration(count: number, unit: CountedUnit, lang: DocLang): string {
  if (lang !== 'ar') return `${count} ${unit}${count === 1 ? '' : 's'}`;

  const forms = COUNTED[unit];
  if (count === 1) return forms.one;
  if (count === 2) return forms.two;
  if (count >= 3 && count <= 10) return `${count} ${forms.few}`;
  return `${count} ${forms.many}`;
}

/**
 * A person's, a site's or a job title's name, in the document's language.
 *
 * Not a translation and not a transliteration — a lookup of the spelling
 * somebody recorded. نور الأمين, نور العامين and نور الامين are all reasonable
 * readings of the same Latin name and exactly one is on the passport, so the
 * system never invents one.
 *
 * Falls back to the Latin spelling rather than to a blank, which is what lets a
 * company fill these in gradually: the document is right where it can be and
 * honest where it cannot, instead of showing a column of gaps.
 */
export function docName(
  english: string | null | undefined,
  arabic: string | null | undefined,
  lang: DocLang,
): string {
  if (lang === 'ar') return (arabic?.trim() || english?.trim()) ?? '';
  return english?.trim() ?? '';
}

/**
 * A worked duration — hours and minutes — in the document's language.
 *
 * Abbreviated in both, because this goes in a table column beside forty others:
 * "8h 00m" in English, "8س 00د" in Arabic, where س is ساعة and د is دقيقة. The
 * spelled-out Arabic (ثماني ساعات) is correct and three times the width, which
 * on a landscape attendance sheet costs a column somebody needed.
 *
 * Zero comes back empty rather than as "0h 00m". A blank cell reads as "none",
 * and a page of zeroes reads as data.
 */
export function docHoursMinutes(minutes: unknown, lang: DocLang): string {
  const total = Number(minutes ?? 0);
  if (!Number.isFinite(total) || total === 0) return '';

  const h = Math.floor(total / 60);
  const m = String(Math.round(total % 60)).padStart(2, '0');

  return lang === 'ar' ? `${h}س ${m}د` : `${h}h ${m}m`;
}

/**
 * Rewrites a span describeGap produced into the document's language.
 *
 * Keyed on the shape that function emits — "2 months 3 days" — rather than on
 * the numbers themselves, so the two stay in step without describeGap having to
 * know that a second language exists.
 */
export function docGap(text: string, lang: DocLang): string {
  if (lang !== 'ar') return text;

  return text.replace(
    /(\d+)\s+(year|month|day)s?/g,
    (_whole, digits: string, unit: string) =>
      docDuration(Number(digits), unit as CountedUnit, 'ar'),
  );
}

/* =============================================================================
 * The unit the money is in
 * -----------------------------------------------------------------------------
 * Every document printed the ISO code — AED — in both languages, because that
 * is what companies.currency holds and nothing translated it. An Arabic payslip
 * headed "AED" is a document in two scripts, and the one word on it a labourer
 * most needs to recognise is the one left in the other alphabet.
 *
 * The codes are kept rather than replaced. An Arabic reader in the Gulf knows
 * د.إ; a bank does not, and neither does a MOHRE filing. So the symbol is used
 * where a person reads it — the masthead of a payslip, a column heading — and
 * the code stays wherever the figure is going somewhere other than a pair of
 * eyes. docCurrency is for the first; company.currency is still there for the
 * second.
 *
 * Only the currencies this application can actually be set to. A code that is
 * not here comes back unchanged, which is the same rule docLabel follows and is
 * far better than guessing at an abbreviation nobody uses.
 * ========================================================================== */
const CURRENCY_AR: Record<string, string> = {
  AED: 'د.إ',
  SAR: 'ر.س',
  QAR: 'ر.ق',
  KWD: 'د.ك',
  BHD: 'د.ب',
  OMR: 'ر.ع',
  EGP: 'ج.م',
  JOD: 'د.أ',
  /* Not translated on purpose. There is no Arabic abbreviation for these that
     a reader would recognise faster than the Latin one, and inventing one is
     worse than leaving it. Listed so the next person does not add one. */
  USD: 'USD',
  EUR: 'EUR',
  GBP: 'GBP',
  INR: 'INR',
  PKR: 'PKR',
};

/**
 * A currency code as a reader of this document would write it.
 *
 * Falls back to the code itself, which is always correct if not always
 * idiomatic.
 */
export function docCurrency(code: string | null | undefined, lang: DocLang): string {
  const unit = (code ?? 'AED').trim().toUpperCase();
  if (lang !== 'ar') return unit;
  return CURRENCY_AR[unit] ?? unit;
}

/**
 * The unit as a *drawn* document shows it.
 *
 * The dirham has a sign of its own now, and a page that can draw it should:
 * it is what the note in somebody's hand will be printed with from here on,
 * and the letters AED beside it would read as a different currency.
 *
 * Told apart from docCurrency deliberately, because the two answer different
 * questions. A spreadsheet keeps the letters — its reader does not have this
 * font installed and would get an empty box in a column of figures — and so
 * does anything a machine parses. Only a PDF, which carries the font inside
 * it, asks for this one. See src/core/dirham.ts.
 */
export function currencySign(code: string | null | undefined, lang: DocLang): string {
  return isDirham(code) ? DIRHAM_SIGN : docCurrency(code, lang);
}

/** Every code this file knows about. For the tests, and for nothing else. */
export function currencyCodes(): string[] {
  return Object.keys(CURRENCY_AR);
}

/**
 * A heading or a system-generated word, in the document's language.
 *
 * Anything this file has not been taught comes back untouched — which is what
 * keeps names, sites and typed notes out of the translator's way.
 */
export function docLabel<T>(text: T, lang: DocLang): T {
  /* Non-strings pass straight through untouched, which is what keeps a number
     a number. A spreadsheet cell that arrives as the string "1,200.00" cannot
     be summed, sorted or formatted by the person who opens it, and turning
     every figure into text is the quiet way to ruin an export. */
  if (lang !== 'ar' || typeof text !== 'string') return text;
  return (DOC_LABELS[text.trim()] ?? text) as T;
}

/* =============================================================================
 * Whole sentences
 * -----------------------------------------------------------------------------
 * The notes at the foot of a report explain what its figures do and do not
 * cover, and they are what somebody reads when a number looks wrong. A
 * word-by-word lookup cannot touch them: "Expiry is measured against today, so
 * the dates above do not filter this" is one statement, and translating it a
 * word at a time produces something Arabic-shaped that says nothing.
 *
 * So they are matched whole, keyed on the English exactly as the report writes
 * it. Which means a note edited in reportSources.ts silently stops matching and
 * prints in English on an Arabic page — so docStrings.test.ts checks that every
 * note in the codebase is present here, and that edit fails the test run rather
 * than the document.
 *
 * A list of pairs rather than an object literal: these keys are long enough to
 * need joining across lines, and a joined string cannot be an object key.
 * ========================================================================== */

const SENTENCE_PAIRS: Array<[string, string]> = [
  /* Which of the two documents a reader is holding. Said on the sheet, because
     a figure that looks low is otherwise read as a figure that is wrong. */
  [
    'Company payroll only. Labour the company does not employ is not on this '
    + 'document, and the figures above exclude it.',
    'رواتب الشركة فقط. العمالة التي لا توظفها الشركة غير مدرجة في هذا '
    + 'المستند، والمبالغ أعلاه لا تشملها.',
  ],
  /* ---------------------------------------------------------------------------
     The import screen's own prose: what each list holds, and the notes under
     the fields that need one. These are read while somebody decides what their
     own spreadsheet column means, which is the moment a wrong guess becomes a
     thousand wrong rows.
     ------------------------------------------------------------------------ */
  [
    'The staff file — names, identity papers, joining dates and starting pay.',
    'ملف الموظفين — الأسماء وأوراق الهوية وتواريخ الالتحاق والراتب الابتدائي.',
  ],
  [
    'Job numbers, clients, and the coordinates attendance is matched against.',
    'أرقام المشاريع والعملاء والإحداثيات التي يُطابَق عليها الحضور.',
  ],
  [
    'Job titles. Import these before the employees who hold them.',
    'المسميات الوظيفية. استوردها قبل الموظفين الذين يحملونها.',
  ],
  [
    'A pay change for people already on file. The previous rate is closed the '
    + 'day before.',
    'تغيير في الأجر لأشخاص مسجَّلين بالفعل. ويُقفل الأجر السابق في اليوم السابق له.',
  ],
  [
    'Creates the first salary record, effective from the joining date.',
    'يُنشئ أول سجل راتب، ساري اعتباراً من تاريخ الالتحاق.',
  ],
  [
    'Leave the column out unless this person has an agreed rate of their own. '
    + 'Empty means the company rules work it out from their salary.',
    'اترك العمود خارجاً ما لم يكن لهذا الشخص أجر متفق عليه خاص به. والفراغ يعني '
    + 'أن قواعد الشركة تحسبه من راتبه.',
  ],
  [
    'Leave the column out and a number is issued automatically. '
    + 'A number that has been used before is refused, even if that person has left.',
    'اترك العمود خارجاً ويُصدَر الرقم تلقائياً. ويُرفض أي رقم استُخدم من قبل، '
    + 'حتى لو كان صاحبه قد ترك العمل.',
  ],
  [
    'Matched on the designation code or its name. An unknown one stops the row '
    + 'rather than inventing a job title.',
    'يُطابَق على رمز المسمى الوظيفي أو اسمه. والمسمى غير المعروف يوقف الصف بدلاً '
    + 'من اختلاق مسمى وظيفي.',
  ],
  [
    'Matched on the branch code first, then its name. An unknown one stops '
    + 'the row rather than creating an office nobody opened.',
    'يُطابَق على رمز الفرع أولاً ثم اسمه. والفرع غير المعروف يوقف الصف بدلاً من '
    + 'إنشاء مكتب لم يفتحه أحد.',
  ],
  [
    'The trade a person works in — electrical, civil, plumbing. Matched on '
    + 'the name the company gave it, in either language.',
    'المهنة التي يعمل بها الشخص — كهرباء، مدني، سباكة. يُطابَق على الاسم الذي '
    + 'أعطته الشركة له، بأي من اللغتين.',
  ],
  [
    'Gratuity, leave entitlement and length of service are all counted from here.',
    'تُحتسب مكافأة نهاية الخدمة واستحقاق الإجازة ومدة الخدمة كلها من هنا.',
  ],
  [
    'Format the column as Text in Excel before typing, or the leading 784 '
    + 'and any dashes are rewritten by the spreadsheet.',
    'اضبط تنسيق العمود على "نص" في إكسل قبل الكتابة، وإلا أعاد الجدول كتابة الـ '
    + '784 في المقدمة وأي شرطات.',
  ],
  [
    'Kept as text. A bank account read as a number loses its last digits, '
    + 'which is a salary paid to nobody.',
    'يُحفظ كنص. فحساب مصرفي يُقرأ كرقم يفقد آخر خاناته، وهذا راتب يُدفع إلى لا أحد.',
  ],
  [
    'Leave the column out and a number is issued automatically. '
    + 'A number used before is refused, so old job costing keeps its meaning.',
    'اترك العمود خارجاً ويُصدَر الرقم تلقائياً. ويُرفض أي رقم استُخدم من قبل، '
    + 'لتبقى تكاليف المشاريع القديمة على معناها.',
  ],
  [
    'Matched on employee number or name.',
    'يُطابَق على رقم الموظف أو اسمه.',
  ],
  /* The advances and the leave, from 0213. */
  [
    'Matched on employee number first, then on name.',
    'يُطابَق على رقم الموظف أولاً ثم على الاسم.',
  ],
  /* The daily attendance email. */
  [
    'Daily attendance',
    'الحضور اليومي',
  ],
  [
    'Nobody recorded a day for these',
    'لم يُسجل لهؤلاء يوم',
  ],
  [
    'Marked outside a site boundary',
    'سُجّل خارج نطاق الموقع',
  ],
  [
    'Nobody is on the roster for this day.',
    'لا أحد على القائمة في هذا اليوم.',
  ],
  [
    'Sent every evening from NoorOne. The full day is attached as a '
    + 'spreadsheet; the monthly grid in the app is the record.',
    'تُرسل كل مساء من NoorOne. اليوم كاملاً مرفق كجدول؛ '
    + 'والجدول الشهري في التطبيق هو السجل.',
  ],
  [
    'Empty means the whole amount is recovered from the next payroll.',
    'الفراغ يعني استقطاع المبلغ كاملاً من مسيرة الرواتب القادمة.',
  ],
  [
    'The first payroll month the deduction comes out of.',
    'أول شهر تُستقطع منه القيمة.',
  ],
  [
    'Money already handed over is disbursed; one fully recovered is settled.',
    'المبلغ المُسلّم فعلاً يكون مصروفاً، والمُستقطع بالكامل يكون مُسدّداً.',
  ],
  [
    'Left out, it is counted from the two dates.',
    'إن تُرك فارغاً حُسب من التاريخين.',
  ],
  [
    'Only approved and taken days count as leave anywhere else in the system.',
    'الأيام المعتمدة والمأخوذة وحدها تُحسب إجازة في بقية النطام.'.replace('النطام', 'النظام'),
  ],
  [
    'With longitude, draws the site boundary attendance is matched against. '
    + 'Both are needed, or neither.',
    'مع خط الطول، يرسم حدود الموقع التي يُطابَق عليها الحضور. وكلاهما مطلوب، أو '
    + 'لا واحد منهما.',
  ],
  [
    'Empty uses the company default.',
    'الفراغ يستخدم الإعداد الافتراضي للشركة.',
  ],
  /* The rest of a site's own row, which the site file carries so a company can
     put its projects back without setting the hours by hand again. */
  [
    'A clock reading — 06:00. Arriving after it is marked late. '
    + 'Empty means the company rule.',
    'وقت بالساعة — 06:00. ومن يحضر بعده يُسجل متأخراً. '
    + 'والفراغ يعني قاعدة الشركة.',
  ],
  [
    'Leaving before it is marked as going early. Empty: a short day is '
    + 'judged on the hours worked instead.',
    'من ينصرف قبله يُسجل مغادراً مبكراً. والفراغ: اليوم القصير '
    + 'يُحكم عليه بساعات العمل بدلاً من ذلك.',
  ],
  [
    'Minutes either side that still count. Empty uses the company grace.',
    'الدقائق قبله وبعده التي تُحتسب. والفراغ يستخدم سماح الشركة.',
  ],
  [
    'The share of the budget at which the screens start warning. Empty: 80.',
    'نسبة الميزانية التي تبدأ عندها الشاشات بالتنبيه. والفراغ: २०.'.replace('२०', '80'),
  ],
  [
    'The site as it is written in Arabic, where somebody has typed it. '
    + 'The job number is never translated.',
    'اسم الموقع بالعربية حيث كتبه أحد. '
    + 'ورقم المشروع لا يُترجم أبداً.',
  ],
  [
    'Matched on employee number first, then on name. A name that matches two '
    + 'people stops the row — it will not guess which one got the raise.',
    'يُطابَق على رقم الموظف أولاً ثم على الاسم. والاسم الذي يطابق شخصين يوقف '
    + 'الصف — فلن يخمّن أيهما نال الزيادة.',
  ],
  /* ---------------------------------------------------------------------------
     What the attendance and payroll calculators say when nothing needs a
     number. Their numbered cousins are in FIGURE_PAIRS below.
     ------------------------------------------------------------------------ */
  [
    'Driving outside the scanned hours was not counted — travel time is not paid.',
    'لم تُحتسب القيادة خارج ساعات التسجيل — فوقت التنقل غير مدفوع.',
  ],
  [
    'Still on site — the day is not finished and no hours are counted yet.',
    'ما زال في الموقع — لم ينتهِ اليوم ولم تُحتسب أي ساعات بعد.',
  ],
  [
    'An overlapping stay was discarded.',
    'استُبعدت فترة تواجد متداخلة.',
  ],
  [
    'Time was recorded outside every known site boundary, and no other site '
    + 'was worked that day, so these hours are charged to no job number.',
    'سُجّل وقت خارج حدود كل موقع معروف، ولم يُعمل في أي موقع آخر ذلك اليوم، '
    + 'فلم تُحمَّل هذه الساعات على أي رقم مشروع.',
  ],
  [
    'Air tickets already paid for by the company. Shown for reference; '
    + 'no part of this total is deducted below.',
    'تذاكر طيران سبق أن دفعتها الشركة. تُعرض للاطلاع فقط، ولا يُخصم أي جزء من هذا '
    + 'الإجمالي أدناه.',
  ],
  [
    'Nobody in this section for this period.',
    'لا أحد في هذا القسم عن هذه الفترة.',
  ],
  [
    'No outside labour was charged this period.',
    'لم تُحمَّل أي عمالة من خارج الشركة على هذه الفترة.',
  ],
  [
    'A stay matched no boundary and was charged to the site worked earlier that day.',
    'لم تطابق فترة التواجد أي حدود، فحُمّلت على الموقع الذي عُمل فيه سابقاً ذلك اليوم.',
  ],
  [
    'A stay matched no boundary and was charged to the site worked the previous day.',
    'لم تطابق فترة التواجد أي حدود، فحُمّلت على الموقع الذي عُمل فيه في اليوم السابق.',
  ],
  [
    'One day was shorter than the standard day and is paid pro rata.',
    'كان يوم واحد أقصر من اليوم القياسي ويُدفع بالتناسب.',
  ],
  [
    'Deductions exceed earnings this period — net pay is negative.',
    'تتجاوز الاستقطاعات المستحقات في هذه الفترة — صافي الأجر بالسالب.',
  ],
  /* ---------------------------------------------------------------------------
     The notes on the payroll and settlement workbooks.

     These are the italic lines under a table — the ones that say why a figure
     is what it is, or that it is wrong. They were written in English at the
     point they were built, so an Arabic workbook came out with Arabic headings
     over English explanations, which is the worst of both: it looks translated
     and cannot be read.
     ------------------------------------------------------------------------ */
  [
    'No attendance was recorded against any job number this month.',
    'لم يُسجَّل أي حضور على أي رقم مشروع هذا الشهر.',
  ],
  [
    'These figures do not add up: {gross} less {deducted} less {paid} is '
    + '{sum}, but the net below says {net}. Do not issue this payslip — '
    + 'tell whoever maintains the system.',
    'هذه الأرقام لا تتطابق: {gross} ناقص {deducted} ناقص {paid} يساوي {sum}، '
    + 'لكن الصافي أدناه يقول {net}. لا تصرف هذه القسيمة — أبلغ من يتولى صيانة '
    + 'النظام.',
  ],
  [
    '{hours} overtime hours were recorded but not approved, and are not paid here.',
    'سُجّلت {hours} ساعة عمل إضافي دون اعتماد، وهي غير مدفوعة هنا.',
  ],
  [
    'Advance recovery — advance of {date}',
    'سداد سلفة — سلفة بتاريخ {date}',
  ],
  [
    'Advance recovery',
    'سداد سلفة',
  ],
  [
    '{amount} of pay is not charged to any job number. That labour is '
    + 'uncosted — check for attendance with no site against it.',
    '{amount} من الأجر غير محمَّل على أي رقم مشروع. هذه العمالة بلا تكلفة — '
    + 'راجع الحضور المسجَّل بلا موقع.',
  ],
  [
    'priced on an overtime salary of {salary}, not the full monthly salary',
    'محسوب على راتب عمل إضافي قدره {salary}، لا على الراتب الشهري كاملاً',
  ],

  /* The settlement sheet — the document somebody signs on their last day. */
  [
    'Delete this row, then paste yours',
    'احذف هذا السطر ثم الصق بياناتك',
  ],
  [
    'The employee code exactly as this system holds it. That is what matches '
    + 'the row to a person; the name is only there so you can read the sheet.',
    'رمز الموظف كما هو مسجَّل في النظام تماماً. هو ما يربط السطر بالشخص؛ '
    + 'والاسم موجود لتتمكن من قراءة الورقة فقط.',
  ],
  [
    'For your eyes. Nothing is matched on it.',
    'للقراءة فقط، ولا يُربط به شيء.',
  ],
  [
    'Leave a day blank when you are not recording it. A blank day is left '
    + 'alone; it is not an absence.',
    'اترك اليوم فارغاً إذا لم تكن تسجله. اليوم الفارغ يُترك كما هو، ولا '
    + 'يُعتبر غياباً.',
  ],
  [
    'The job number the day is charged to. Leave it blank if the day was not '
    + 'on a site.',
    'رقم المشروع المحمّل عليه اليوم. اتركه فارغاً إذا لم يكن اليوم في موقع.',
  ],
  /* Reworded once the write path was built. "Blank means none" against APRVD
     was wrong in a way that costs money: a company that fills in one overtime
     column and not the other would have had every hour of it unpaid. */
  [
    'Approved overtime for that day, in hours. This is the figure that gets '
    + 'paid. Leave it blank and the recorded overtime is paid as it stands; '
    + 'write 0 to approve none of it.',
    'الوقت الإضافي المعتمد لذلك اليوم بالساعات. هذا هو الرقم الذي يُصرف. '
    + 'اتركه فارغاً ليُصرف الوقت الإضافي المسجل كما هو، '
    + 'واكتب 0 لعدم اعتماد أي منه.',
  ],
  [
    'Overtime actually recorded, in hours. Fill in either column, or both.',
    'الوقت الإضافي المسجل فعلياً بالساعات. املأ أحد العمودين أو كليهما.',
  ],
  [
    'A cell is refused, and named by its row and column, when the status is a '
    + 'letter that is not listed, when an overtime figure is not a number or '
    + 'is more than 24, or when a row has a name and no employee ID. The rest '
    + 'of the sheet still imports: one bad cell never costs you the row.',
    'تُرفض الخانة، ويُذكر سطرها وعمودها، إذا كانت الحالة حرفاً غير مذكور، أو '
    + 'كان رقم الوقت الإضافي ليس رقماً أو تجاوز 24، أو كان السطر يحمل اسماً '
    + 'بلا رقم موظف. وبقية الورقة تُستورد كالمعتاد: خانة واحدة خاطئة لا '
    + 'تُفقدك السطر.',
  ],
  [
    'Already filled in. Do not move the columns or change the dates — every '
    + 'group of four belongs to the date merged above it.',
    'مُعبَّأة مسبقاً. لا تُحرّك الأعمدة ولا تُغيّر التواريخ — كل مجموعة من '
    + 'أربعة أعمدة تخص التاريخ المدموج فوقها.',
  ],
  [
    'Nothing outstanding — every month up to the last working day has been paid.',
    'لا مستحقات متأخرة — كل شهر حتى آخر يوم عمل قد صُرف.',
  ],
  [
    'Leave salary: {worked} days worked ÷ 365 × 30 days = {earned} days, '
    + 'at {rate} a day on {base} = {amount}.',
    'بدل الإجازة: {worked} يوم عمل ÷ 365 × 30 يوماً = {earned} يوماً، بواقع '
    + '{rate} لليوم على {base} = {amount}.',
  ],
  [
    'Less {deducted} days: leave taken last time beyond what was approved. '
    + 'Those days are deducted from the leave earned, not from pay — '
    + 'leaving {left} days at {rate} a day.',
    'ناقص {deducted} يوماً: إجازة أُخذت المرة الماضية زيادة على المعتمد. تُخصم '
    + 'هذه الأيام من الإجازة المستحقة لا من الأجر — فيبقى {left} يوماً بواقع '
    + '{rate} لليوم.',
  ],
  [
    'Returned {extra} days after the {approved} approved — {months} month '
    + 'of visa cost at {rate} is deducted below.',
    'عاد بعد {extra} يوماً زيادة على {approved} يوماً معتمدة — يُخصم أدناه شهر '
    + 'واحد من تكلفة التأشيرة بواقع {rate}.',
  ],
  [
    'Returned {extra} days after the {approved} approved — {months} months '
    + 'of visa cost at {rate} is deducted below.',
    'عاد بعد {extra} يوماً زيادة على {approved} يوماً معتمدة — تُخصم أدناه '
    + '{months} أشهر من تكلفة التأشيرة بواقع {rate}.',
  ],
  [
    'No gratuity is due.',
    'لا تستحق مكافأة نهاية الخدمة.',
  ],
  [
    'Every one of the {days} days of service falls inside a band. '
    + 'None is left uncounted.',
    'كل يوم من أيام الخدمة البالغة {days} يوماً يقع داخل شريحة. ولم يبقَ يوم '
    + 'غير محسوب.',
  ],
  /* What an outside worker signs instead. A waiver only makes sense against
     somebody you had a claim on, so his line is a receipt: he was paid this
     amount for this work, which is true and names nobody. See 0145. */
  [
    'I, {name}, confirm that I have received the amount shown above in full '
    + 'for the work set out on this page.',
    'أنا، {name}، أقرّ بأنني استلمت المبلغ المبيَّن أعلاه بالكامل مقابل العمل '
    + 'الموضّح في هذه الصفحة.',
  ],
  [
    'I, {name}, confirm that I have received all my dues and my {document} '
    + 'from {company}, the details of which are set out above.',
    'أنا، {name}، أقر بأنني استلمت كامل مستحقاتي و{document} من {company}، '
    + 'والمفصلة أعلاه.',
  ],
  [
    'I confirm that I have no further claim in respect of this {document} '
    + 'against {company}.',
    'وأقر بأنه لا مطالبة أخرى لي بشأن {document} قِبل {company}.',
  ],
  /* The job cost report's footnote — the paragraph somebody reads precisely
     because a figure surprised them, and the one that was still English on an
     Arabic sheet. Held in three sentences rather than one, because the middle
     one only appears when there were paid days nobody stood on a site for. */
  [
    '{amount} of the pay in the Earned column is not allocated to a job number.',
    '{amount} من الأجر في عمود المستحق غير محمَّل على رقم مشروع.',
  ],
  /* A paid day off is carried to the last site somebody worked on — see
     carryPaidDaysToLastWorkedSite. The two sentences that used to sit here
     counted those days and called them "correctly charged to no site", which
     was true of an older calculator and would now explain away a gap that is a
     real finding. */
  [
    'A paid day off — a public holiday, a weekly holiday or paid leave — is '
    + 'charged to the site the person last worked on, so it is part of this '
    + 'figure only where nothing earlier in the month had put them on a site.',
    'اليوم المدفوع بلا عمل — عطلة رسمية أو عطلة أسبوعية أو إجازة مدفوعة — '
    + 'يُحمَّل على آخر موقع عمِل فيه الشخص، فلا يدخل في هذا الرقم إلا إذا لم '
    + 'يسبقه في الشهر يوم في أي موقع.',
  ],
  [
    'Whatever remains is work done on a day with no job number against it. '
    + 'The Labour with no job number report lists those day by day, with the '
    + 'person and the amount.',
    /* The report is named أيام العمل بلا رقم مشروع on the button that opens it.
       This sentence called it تقرير العمالة بلا رقم مشروع, so a reader sent to
       find it was given a name that is on nothing. */
    'وما تبقى هو عمل أُنجز في يوم بلا رقم مشروع عليه. وتقرير أيام العمل بلا رقم '
    + 'مشروع يسردها يوماً بيوم، مع الشخص والمبلغ.',
  ],
  [
    'Every {currency} earned is allocated to a job number.',
    'كل {currency} مستحق محمَّل على رقم مشروع.',
  ],
  /* The advance line on a payslip. It is the sentence that explains why the
     deduction is what it is, and it was assembled from three fragments — so it
     could never match an entry and went out in English on every Arabic slip. */
  [
    'includes {recovered} recovered against advances, leaving {remaining} outstanding',
    'تشمل {recovered} مستردة من السلف، ويتبقى {remaining}',
  ],
  [
    'includes {recovered} recovered against advances, now cleared',
    'تشمل {recovered} مستردة من السلف، وقد سُدّدت بالكامل',
  ],
  /* The dashboard's two footnotes. They say which of two things the figures
     are — headcounts on one day, or man-days over a period — and that is the
     difference between eleven people being absent and one person being absent
     for eleven days. Untranslated, the sheet said it only to English readers. */
  [
    'Headcounts on the day named above.',
    'أعداد الحاضرين في اليوم المذكور أعلاه.',
  ],
  [
    'Person-days over the period named above, not headcounts: one person '
    + 'absent for eleven days is eleven here, and one.',
    'أيام عمل خلال الفترة المذكورة أعلاه، لا أعداد أشخاص: شخص واحد غائب '
    + 'أحد عشر يوماً يُحتسب هنا أحد عشر، وهو شخص واحد.',
  ],
  [
    'A staff file is a snapshot of today, so the dates above do not filter it.',
    'ملف الموظفين يعكس الوضع اليوم، ولذلك لا تُطبَّق عليه التواريخ أعلاه.',
  ],
  [
    'Expiry is measured against today, so the dates above do not filter this.',
    'يُحتسب تاريخ الانتهاء بالنسبة إلى اليوم، فلا تُطبَّق عليه التواريخ أعلاه.',
  ],
  [
    'Overtime is the approved figure where an approval exists and the recorded '
    + 'figure otherwise — the same rule payroll applies.',
    'يُحتسب العمل الإضافي بالقيمة المعتمدة عند وجود اعتماد، وبالقيمة المسجلة فيما '
    + 'عدا ذلك — وهي القاعدة نفسها المطبقة في الرواتب.',
  ],
  [
    'Every row is a present, paid day whose hours reached no job number. The '
    + 'company carried this cost against nothing.',
    'كل سطر هنا يوم حضور مدفوع لم تُنسب ساعاته إلى أي رقم مشروع، أي تكلفة تحمّلتها '
    + 'الشركة دون أن تُحمَّل على جهة.',
  ],
  [
    '"No site on the day at all" is usually a day marked by hand, or a '
    + 'roll-call nobody attached a job to. "Part of the day fell outside every '
    + 'boundary" is usually a site boundary drawn too small — widen it and '
    + 'recalculate the day.',
    '«لا يوجد موقع لليوم إطلاقاً» تعني غالباً يوماً سُجّل يدوياً أو نداءً لم يُربط '
    + 'بمشروع. و«جزء من اليوم خارج كل النطاقات» تعني غالباً نطاق موقع مرسوماً أضيق '
    + 'مما يجب — وسّعه ثم أعد احتساب اليوم.',
  ],
  [
    'Public holidays, weekly holidays and paid leave are deliberately absent. '
    + 'Those are earned and unallocated because nobody was on a site, which is '
    + 'correct and needs no action.',
    'العطل الرسمية والأسبوعية والإجازات المدفوعة غير مدرجة عن قصد، فهي مستحقة وغير '
    + 'مخصصة لأن أحداً لم يكن في موقع، وهذا صحيح ولا يستدعي أي إجراء.',
  ],
  [
    'Amounts are priced at the rate on the payslip covering that day. A day no '
    + 'payroll run has reached yet shows hours with no amount, rather than an '
    + 'estimate somebody would quote back.',
    'تُسعَّر المبالغ بالأجر الوارد في قسيمة الراتب التي تغطي ذلك اليوم. واليوم الذي '
    + 'لم تصل إليه أي دورة رواتب بعد تظهر ساعاته دون مبلغ، بدلاً من تقدير قد '
    + 'يُستشهد به لاحقاً.',
  ],
  [
    'Deductions include loan and advance recovery. Gross less deductions less '
    + 'anything already paid is the net.',
    'تشمل الاستقطاعات سداد القروض والسلف. وصافي المستحق هو الإجمالي بعد خصم '
    + 'الاستقطاعات وما تم دفعه مسبقاً.',
  ],

  [
    'P is a day present, A absent, L on leave, H a public holiday and R the '
    + 'weekly rest day. The site, the times somebody came and went and the '
    + 'overtime of each day are in the Excel download of the same month.',
    'الرمز P يعني يوم حضور، وA غياب، وL إجازة، وH عطلة '
    + 'رسمية، وR يوم الراحة الأسبوعية. أما الموقع وأوقات '
    + 'الحضور والانصراف والساعات الإضافية لكل يوم فهي في ملف '
    + 'الإكسل للشهر نفسه.',
  ],

  /* ---- filled in by the translator ---------------------------------------
     scripts/translate-doc-strings.mjs appends whole sentences here. Same rule
     as the labels table: it writes below this line and nowhere else.
     -------------------------------------------------------------------- */
  [
    'A star beside a status means a mark that day fell outside every site '
    + 'boundary.',
    'وجود نجمة بجانب الحالة يعني أن إحدى علامات ذلك اليوم سُجلّت خارج '
    + 'نطاق كل المواقع.',
  ],
  [
    'Somebody nobody recorded a day for at all is not on this sheet. The daily '
    + 'attendance email starts from the roster and lists them, because that is '
    + 'read the same evening.',
    'من لم يُسجل له يوم إطلاقاً لا يظهر في هذا الكشف. أما بريد '
    + 'الحضور اليومي فيبدأ من كشف العاملين ويسردهم، لأنه يُقرأ في '
    + 'مساء اليوم نفسه.',
  ],

  [
    'No payroll has been run for this employee, so no unpaid salary is in this '
    + 'settlement. Run payroll and the months will appear here.',
    'لم تُحتسب أي مستحقات لهذا الموظف، فلا يوجد راتب غير مدفوع في هذه '
    + 'التصفية. احتسِب المستحقات وستظهر الأشهر هنا.',
  ],

  // TRANSLATOR ANCHOR — sentences. Nothing may be written below this line.
];

/* =============================================================================
 * Sentences that arrive with their figures already in them
 * -----------------------------------------------------------------------------
 * The calculators write their own warnings, and they write them with the
 * numbers built in:
 *
 *     "Advance recovery reduced from 1000.00 to 515.14 by the 20% cap."
 *
 * Every one is a string no dictionary can hold, because the next employee makes
 * a different one. They reach a payslip's remarks, the settlement sheet and the
 * attendance day view, so an Arabic payslip carried an English explanation of
 * the single line on it people query most.
 *
 * The other notes in this file were fixed by looking the sentence up first and
 * putting the figures in after. That cure does not reach here: these are made
 * deep inside pure arithmetic that has no idea what language anything will be
 * read in, and threading one through would put a display concern into the one
 * part of the system that is worth keeping free of it.
 *
 * So the numbers come back out. A sentence with no exact entry has its figures
 * lifted, leaving a shape — "Advance recovery reduced from {n} to {n} by the
 * {n}% cap." — and the shape is what is looked up. The Arabic puts them back by
 * position, {1} {2} {3} in whatever order Arabic wants them, which is not
 * always the order English had.
 * ========================================================================== */

/**
 * What gets lifted out before a sentence is looked up.
 *
 * A run of digits — 1,000.00, 20, 7.5 — and anything in double quotes. The
 * quoted part is there for the codes and names these warnings carry: a leave
 * type, an employee's own leave name. Quoting them at the call site is what
 * makes one dictionary entry cover every leave type instead of needing one
 * each, and quotation marks around a code read correctly in both languages.
 */
const FIGURE = /"[^"]*"|\b(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sept?|Oct|Nov|Dec)-\d{2}\b|\d+(?:,\d{3})*(?:\.\d+)?/g;

/**
 * A payroll month as formatMonth writes it — "Aug-26", "Sept-26".
 *
 * Lifted whole by FIGURE above rather than as the 26 inside it. Taken a digit
 * at a time, "2 months unpaid: Aug-26 to Sept-26" left "Aug-" and "Sept-" in
 * the shape, which no entry can ever match, so the note under an Arabic
 * settlement's salaries came out in English however the dictionary was
 * written.
 */
const MONTH_LABEL = /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sept?|Oct|Nov|Dec)-(\d{2})$/;
const MONTH_KEYS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A payroll month in the document's language: "Aug-26" as "أغسطس 2026".
 *
 * Not "أغسطس-26", which was the first attempt. A hyphen between an Arabic word
 * and a number belongs to the number as far as right-to-left ordering is
 * concerned, and it printed as "أغسطس26-", the dash hanging off the far end.
 * A space and the whole year is how the month is written in Arabic anyway —
 * the century is 20: formatMonth only ever sees payroll periods. Anything not
 * in formatMonth's shape comes back untouched.
 */
export function docMonth(label: string, lang: DocLang): string {
  if (lang !== 'ar') return label;
  const match = MONTH_LABEL.exec(label.trim());
  if (!match) return label;
  const month = MONTH_KEYS.indexOf(match[1] === 'Sept' ? 'Sep' : match[1] ?? '');
  return `${monthName(month + 1, 'ar')} 20${match[2]}`;
}

/**
 * Templates keyed by shape. Written with {1}, {2}, {3} in the English so the
 * Arabic can move them; the key is derived by flattening those to {n}.
 */
const FIGURE_PAIRS: Array<[string, string]> = [
  /* ---- what the attendance day says ------------------------------------- */
  [
    '{1} minutes of paid break are counted as worked — they were marked out '
    + 'for a break the rules pay for.',
    'تُحتسب {1} دقيقة من الاستراحة المدفوعة ضمن ساعات العمل — فقد سُجّل الخروج '
    + 'لاستراحة تدفعها اللائحة.',
  ],
  [
    'Back {1} minutes late from the break; that time has been deducted.',
    'تأخر {1} دقيقة في العودة من الاستراحة؛ وقد خُصمت هذه المدة.',
  ],
  [
    'Overtime rounded down by {1} minutes: {2}h worked, {3}h paid, on the '
    + "company's {4}-minute step.",
    'قُرّب العمل الإضافي إلى الأدنى بمقدار {1} دقيقة: {2} ساعة عمل، و{3} ساعة '
    + 'مدفوعة، على خطوة الشركة البالغة {4} دقائق.',
  ],
  [
    'Overtime rounded up by {1} minutes: {2}h worked, {3}h paid, on the '
    + "company's {4}-minute step.",
    'قُرّب العمل الإضافي إلى الأعلى بمقدار {1} دقيقة: {2} ساعة عمل، و{3} ساعة '
    + 'مدفوعة، على خطوة الشركة البالغة {4} دقائق.',
  ],
  [
    'Overtime rounded down by {1} minute: {2}h worked, {3}h paid, on the '
    + "company's {4}-minute step.",
    'قُرّب العمل الإضافي إلى الأدنى بمقدار دقيقة واحدة: {2} ساعة عمل، و{3} ساعة '
    + 'مدفوعة، على خطوة الشركة البالغة {4} دقائق.',
  ],
  [
    'Overtime rounded up by {1} minute: {2}h worked, {3}h paid, on the '
    + "company's {4}-minute step.",
    'قُرّب العمل الإضافي إلى الأعلى بمقدار دقيقة واحدة: {2} ساعة عمل، و{3} ساعة '
    + 'مدفوعة، على خطوة الشركة البالغة {4} دقائق.',
  ],
  /* A stay whose two ends are on different days. The figures are the count and
     the longest of them, in hours — see LONGEST_STAY_MINUTES in calc/attendance
     and migration 0200 for how one came to be written at all. */
  [
    'Stays that ran longer than a day were not counted: {1} of them, the '
    + 'longest claiming {2} hours. A scan taken on another day has closed a '
    + 'shift on this one — correct the times here and the hours will follow.',
    'لم تُحتسب الفترات التي تجاوزت اليوم الواحد: عددها {1}، '
    + 'وأطولها يدّعي {2} ساعة. مسح من يوم آخر أغلق وردية في هذا '
    + 'اليوم — صحّح الأوقات هنا وستتبعها الساعات.',
  ],
  [
    'Overtime of {1}h exceeds the {2}h daily cap.',
    'العمل الإضافي البالغ {1} ساعة يتجاوز الحد اليومي البالغ {2} ساعة.',
  ],
  [
    'Overtime of {1}h this period exceeds the {2}h period cap.',
    'العمل الإضافي البالغ {1} ساعة في هذه الفترة يتجاوز حد الفترة البالغ {2} ساعة.',
  ],
  [
    'Overtime worked on {1} days this period exceeds the {2}-day period cap.',
    'العمل الإضافي في {1} يوماً خلال هذه الفترة يتجاوز حد الفترة البالغ {2} يوماً.',
  ],

  /* ---- what the payroll run says ---------------------------------------- */
  [
    'Marked in while on {1} leave — recorded present because they were '
    + 'scanned on site.',
    'سُجّل الدخول أثناء إجازة {1} — واعتُبر حاضراً لأنه مسجَّل في الموقع.',
  ],
  [
    '{1} is paid as a lump through a settlement, not in the wage for this '
    + 'month. The days are shown here so the month reads correctly, and they '
    + 'add nothing to the pay.',
    '{1} يُدفع دفعةً واحدة عبر التسوية، لا ضمن أجر هذا الشهر. تظهر الأيام هنا '
    + 'ليكون الشهر صحيحاً، ولا تضيف شيئاً إلى الأجر.',
  ],
  /* Federal Decree-Law 33 of 2021: unpaid leave is not service. Said on the
     settlement so the subtraction is visible rather than a total that is
     simply smaller than the dates above it imply. */
  [
    '{1} day(s) of approved unpaid leave do not count toward the service that '
    + 'earns gratuity, so the service is worked out to {2} rather than {3}.',
    '{1} يوم/أيام من الإجازة غير مدفوعة الأجر المعتمدة لا تُحتسب ضمن مدة '
    + 'الخدمة المستحقة لمكافأة نهاية الخدمة، لذا تُحتسب الخدمة حتى {2} بدلاً '
    + 'من {3}.',
  ],
  /* The foot of a settlement, so a man in another country has somebody to
     telephone rather than somebody to consult. Carries a hole, so it lives
     with the figure templates rather than the whole sentences. See 0175. */
  [
    'Any question about this document: {1}',
    'لأي استفسار بخصوص هذا المستند: {1}',
  ],
  [
    'Leave type {1} is not defined in the current rules.',
    'نوع الإجازة {1} غير معرَّف في القواعد الحالية.',
  ],
  [
    '{1} days were shorter than the standard day and are paid pro rata.',
    '{1} أيام كانت أقصر من اليوم القياسي وتُدفع بالتناسب.',
  ],
  [
    '{1} day(s) of {2} fall past the {3}-day entitlement for this service year '
    + 'and are unpaid.',
    '{1} يوماً من {2} تتجاوز الاستحقاق البالغ {3} يوماً لسنة الخدمة هذه وهي غير '
    + 'مدفوعة.',
  ],
  [
    '{1} day(s) of {2} fall past the configured bands for this service year '
    + 'and are unpaid.',
    '{1} يوماً من {2} تتجاوز الشرائح المضبوطة لسنة الخدمة هذه وهي غير مدفوعة.',
  ],
  [
    "Overtime paid at this employee's own agreed rate of {1} an hour, not the "
    + 'rate derived from their salary.',
    'يُدفع العمل الإضافي بالأجر المتفق عليه لهذا الموظف وقدره {1} للساعة، لا '
    + 'بالأجر المشتق من راتبه.',
  ],
  [
    'Advance recovery reduced from {1} to {2} by the {3}% cap.',
    'خُفّض سداد السلفة من {1} إلى {2} بموجب الحد البالغ {3}%.',
  ],
  [
    'Advance recovery reduced from {1} to {2} by the {3}% cap or by what was '
    + 'left after the other deductions.',
    'خُفّض سداد السلفة من {1} إلى {2} بموجب الحد البالغ {3}% أو بما تبقّى بعد '
    + 'الخصومات الأخرى.',
  ],

  /* The three a recovery somebody decided by hand can write — 0219. The man
     holding the payslip asks why the figure is not the one he was told, and
     these are the answer, in the language the document is printed in. */
  /* A settlement that carries the unpaid months' own deductions — 0219's
     neighbour in the same conversation. See computeSettlement. */
  [
    'The latest payslip is {1}. Payroll has not been run for the months after '
    + 'it, so they are not in this settlement — run it and the missing months '
    + 'will appear here.',
    'آخر قسيمة راتب هي {1}. ولم تُحتسب المستحقات للأشهر التالية لها، '
    + 'فهي غير مدرجة في هذه التصفية — احتسِبها وستظهر الأشهر الناقصة هنا.',
  ],
  [
    'The unpaid payslips recover {1} of advances and only {2} is outstanding. '
    + 'The larger figure was not taken.',
    'تسترد قسائم الرواتب غير المدفوعة {1} من السلف، والمستحق {2} فقط. '
    + 'ولم يُؤخذ المبلغ الأكبر.',
  ],
  [
    '{1} instalment {2}',
    'قسط {1}، {2}',
  ],
  /* And the same instalment where the advance ledger already has it: a period
     posts its repayments when it is locked, which is before it is paid. */
  [
    '{1} instalment {2} already recovered',
    'قسط {1}، {2}، مُستردّ مسبقاً',
  ],
  [
    '{1} still outstanding',
    '{1} لا يزال مستحقاً',
  ],
  /* The extra money inside an unpaid month's salary, named on the settlement
     rather than absorbed into its total. What it was FOR is a piece of its own
     and is left in the words the office typed. */
  [
    'Additional {1}',
    'إضافي {1}',
  ],
  [
    'Advance recovery of {1} set by the payroll office; {2} was scheduled.',
    'حُدّد سداد السلفة بـ {1} من إدارة الرواتب؛ والمجدول كان {2}.',
  ],
  /* The additions total, where the office set it rather than the entered rows.
     Its reason is a separate piece of the same remark and is left in the words
     somebody typed — see docRemark and `because` in calc/payroll.ts. */
  [
    'Additions set to {1} by the payroll office.',
    'حُدّدت الإضافات بـ {1} من إدارة الرواتب.',
  ],
  [
    'No advance recovery this month, set by the payroll office. {1} was '
    + 'scheduled and stays on the balance.',
    'لا خصم من السلفة هذا الشهر بقرار من إدارة الرواتب. وكان المجدول {1} '
    + 'ويبقى على الرصيد.',
  ],
  [
    'Advance recovery reduced from {1} to {2} — that is what was owed, or what '
    + 'was left after the other deductions.',
    'خُفّض سداد السلفة من {1} إلى {2} — وهذا ما كان مستحقاً، أو ما تبقّى بعد '
    + 'الخصومات الأخرى.',
  ],
  [
    'Advance recovery reduced from {1} to {2} — there was not that much left '
    + 'on this payout. A daily-wage worker has no later payslip to carry the '
    + 'balance to.',
    'خُفّض سداد السلفة من {1} إلى {2} — لم يتبقَّ هذا القدر في هذه الدفعة. '
    + 'العامل باليومية ليس لديه كشف راتب لاحق يُرحَّل إليه الرصيد.',
  ],

  /* ---- how long somebody served, and the bands that pay for it ---------- */
  [
    '{1} Years {2} Months {3} Days',
    '{1} سنة و{2} شهراً و{3} يوماً',
  ],
  [
    'First {1} years — {2} days a year',
    'أول {1} سنوات — {2} يوماً في السنة',
  ],
  [
    'After {1} years — {2} days a year',
    'بعد {1} سنوات — {2} يوماً في السنة',
  ],
  [
    '{1} month unpaid: {2} to {3}',
    'شهر واحد غير مدفوع: من {2} إلى {3}',
  ],
  [
    '{1} months unpaid: {2} to {3}',
    '{1} أشهر غير مدفوعة: من {2} إلى {3}',
  ],
  /* An amount somebody agreed rather than one this system worked out. The
     calculation still travels with it, as the second half of the same remark —
     see agreedOrWorkedOut in calc/settlement. */
  [
    'An agreed amount. The calculation comes to {1}',
    'مبلغ متفق عليه. والاحتساب يبلغ {1}',
  ],
  [
    '{1} days of service',
    '{1} يوم خدمة',
  ],

  /* ---- how good the fix on this phone is ---------------------------------
     Read while somebody is standing on a site holding a phone up, deciding
     whether to walk into the open. It is the one message in this app whose
     reader is outdoors, in a hurry, and cannot ask anybody. */
  [
    'Accurate to ±{1} m',
    'دقة ±{1} م',
  ],
  [
    '±{1} m — usable, but stand still a moment longer',
    '±{1} م — صالحة، لكن اثبت مكانك لحظة أطول',
  ],
  [
    '±{1} m — too vague for a {2} m boundary. Move into the open and try again.',
    '±{1} م — غير كافية لحدود {2} م. انتقل إلى مكان مكشوف وحاول مرة أخرى.',
  ],

  /* ---- what the rules screen says when a setting is missing -------------- */
  [
    'Company rules are incomplete — {1} setting(s) still unset: ',
    'قواعد الشركة غير مكتملة — {1} إعداداً ما زال غير محدد: ',
  ],
  [
    ', and {1} more',
    '، و{1} أخرى',
  ],

  /* ---- the notification test --------------------------------------------- */
  [
    '{1} went through the same rule a real one would.',
    'مرّ {1} بالقاعدة نفسها التي يمر بها التنبيه الحقيقي.',
  ],
  [
    '{1} — if it has never been deployed: supabase functions deploy send-push',
    '{1} — إن لم يُنشر قط: supabase functions deploy send-push',
  ],
  [
    'On its way to {1}.',
    'في طريقه إلى {1}.',
  ],

  /* ---- what the import says about a row it cannot take -------------------
     Read by somebody with the spreadsheet open beside them, deciding what to
     change. `{field}` is filled in by issueText with the field's own name,
     because a name baked into the sentence would match no entry.
     ------------------------------------------------------------------------ */
  [
    '{1} is not a date.',
    '{1} ليس تاريخاً.',
  ],
  [
    '{1} read as {2} — day first. The file does not say which was meant.',
    '{1} قُرئ على أنه {2} — اليوم أولاً. والملف لا يبيّن أيهما قُصد.',
  ],
  [
    '{1} read as {2} — month first. The file does not say which was meant.',
    '{1} قُرئ على أنه {2} — الشهر أولاً. والملف لا يبيّن أيهما قُصد.',
  ],
  [
    '{1} has a two-digit year; read as {2}.',
    '{1} سنته من خانتين؛ قُرئ على أنه {2}.',
  ],
  [
    "The cell held the number {1}, which is {2} in Excel's counting.",
    'احتوت الخلية على الرقم {1}، وهو {2} بحساب إكسل.',
  ],
  [
    '{1} is not a number.',
    '{1} ليس رقماً.',
  ],
  [
    '{field} cannot be negative ({1}).',
    '{field} لا يمكن أن يكون بالسالب ({1}).',
  ],
  [
    '{1} read as {2}.',
    '{1} قُرئ على أنه {2}.',
  ],
  [
    '{1} is not a yes or a no.',
    '{1} ليس نعم ولا لا.',
  ],
  [
    '{1} is not a time of day. Write the hour and the minutes, as on a clock.',
    '{1} ليس وقتاً من اليوم. اكتب الساعة والدقائق كما على الساعة.',
  ],
  /* What the daily attendance email says on a lock screen, before anybody has
     opened anything. */
  [
    '{1} on site · {2} absent',
    '{1} في الموقع · {2} غائب',
  ],
  [
    '{1} on site · {2} absent · {3} not marked',
    '{1} في الموقع · {2} غائب · {3} لم يُسجل',
  ],
  [
    '{1} is not one of: {2}.',
    '{1} ليس أحد هذه: {2}.',
  ],
  [
    '{1} was stored as a number and is too long to have survived it — some '
    + 'digits have already changed. Format the column as Text in Excel and '
    + 'enter it again.',
    '{1} حُفظ كرقم وهو أطول من أن ينجو من ذلك — وقد تغيّرت بعض خاناته فعلاً. '
    + 'اضبط تنسيق العمود على "نص" في إكسل وأدخله من جديد.',
  ],
  [
    'Stored as a number, so any leading zero is already gone. Read as {1}.',
    'حُفظ كرقم، فأي صفر في المقدمة قد ضاع بالفعل. قُرئ على أنه {1}.',
  ],
  [
    'Left on {1}, which is before the joining date of {2}.',
    'ترك العمل في {1}، وهو قبل تاريخ الالتحاق {2}.',
  ],
  [
    'Born {1}, which is on or after the joining date. The two columns are '
    + 'probably swapped.',
    'وُلد في {1}، وهو في تاريخ الالتحاق أو بعده. والأرجح أن العمودين متبادلان.',
  ],
  [
    'That birth date makes them {1} at joining.',
    'تاريخ الميلاد هذا يجعل عمره {1} عند الالتحاق.',
  ],
  [
    'That birth date makes them {1} at joining — worth checking.',
    'تاريخ الميلاد هذا يجعل عمره {1} عند الالتحاق — يستحق المراجعة.',
  ],
  [
    'Already expired on {1}. Imported, and it will show on the document report.',
    'منتهٍ بالفعل في {1}. استُورد، وسيظهر في تقرير المستندات.',
  ],
  [
    'Joining date {1} is in the future.',
    'تاريخ الالتحاق {1} في المستقبل.',
  ],
  [
    '{1}, {2} is not a place on earth.',
    '{1}، {2} ليس مكاناً على الأرض.',
  ],
  [
    '{1}, {2} looks like longitude and latitude the wrong way round.',
    '{1}، {2} يبدو أنه خط طول وخط عرض معكوسان.',
  ],
  [
    'Ends {1}, before it starts on {2}.',
    'ينتهي في {1}، قبل أن يبدأ في {2}.',
  ],
  [
    '{field} is required and this row has none.',
    '{field} مطلوب ولا يوجد في هذا الصف.',
  ],
  [
    'The same {field} is already on row {1} of this file. Only the first was '
    + 'kept — decide which is right and import the other separately.',
    '{field} نفسه موجود بالفعل في الصف {1} من هذا الملف. ولم يُحتفظ إلا بالأول '
    + '— قرّر أيهما الصحيح واستورد الآخر على حدة.',
  ],
  [
    'A site needs both coordinates or neither. One on its own places nothing.',
    'الموقع يحتاج الإحداثيتين معاً أو لا شيء. وواحدة بمفردها لا تحدد شيئاً.',
  ],
  [
    'Every mapped column is empty, but the row has data in columns that are '
    + 'not mapped.',
    'كل عمود مرتبط فارغ، لكن الصف يحمل بيانات في أعمدة غير مرتبطة.',
  ],
  [
    'record',
    'سجل',
  ],

  /* ---- what the mapping screen refuses, and why -------------------------- */
  [
    '{1} columns are pointed at {2}. Only one can write to it.',
    '{1} أعمدة موجَّهة إلى {2}. ولا يمكن إلا لواحد أن يكتب فيه.',
  ],
  [
    'No column is mapped to {1}, and a new {2} cannot be created without it.',
    'لا يوجد عمود مرتبط بـ{1}، ولا يمكن إنشاء {2} جديد بدونه.',
  ],
  [
    '{1} is marked required for this import but no column is mapped to it.',
    '{1} محدَّد كمطلوب لهذا الاستيراد لكن لا عمود مرتبط به.',
  ],
  [
    'Nothing in the file identifies an existing record. Map one of: {1}.',
    'لا شيء في الملف يحدد سجلاً قائماً. اربط أحد هذه: {1}.',
  ],
  [
    'One of: {1}.',
    'أحد هذه: {1}.',
  ],

  /* ---- and two the app says when something is plainly wrong -------------- */
  [
    'A period called {1} already exists.',
    'توجد بالفعل فترة باسم {1}.',
  ],
  [
    'The file could not be fetched ({1}).',
    'تعذّر جلب الملف ({1}).',
  ],

  [
    '{1} employees across {2} sites',
    '{1} موظفاً في {2} مواقع',
  ],


  /* ---- the lines along the foot of a document ---------------------------- */
  [
    'Net salary for {1}',
    'صافي الراتب عن {1}',
  ],
  [
    'In words:  {1}',
    'كتابةً:  {1}',
  ],
  [
    '{1}  ·  payable {2}  ·  all figures in {3}',
    '{1}  ·  المستحق {2}  ·  كل المبالغ بـ{3}',
  ],
  /* Two payable figures, never their sum. The company's own payroll and what it
     spent on labour it does not employ are different numbers with different
     readers, and one combined total is the right answer to neither. */
  [
    '{1}  ·  payroll {2}  ·  outside labour {3}  ·  all figures in {4}',
    '{1}  ·  الرواتب {2}  ·  عمالة من خارج الشركة {3}  ·  كل المبالغ بـ{4}',
  ],
  [
    '{1}  ·  {2} of labour  ·  all figures in {3}',
    '{1}  ·  {2} عمالة  ·  كل المبالغ بـ{3}',
  ],
  [
    '{1} leave cycles',
    '{1} دورات إجازة',
  ],
  [
    'Total gratuity — {1} days on basic',
    'إجمالي مكافأة نهاية الخدمة — {1} يوماً على الأساسي',
  ],
  [
    'Total gratuity — {1} days on gross',
    'إجمالي مكافأة نهاية الخدمة — {1} يوماً على الإجمالي',
  ],
  [
    '{1} · Earnings and payments',
    '{1} · المستحقات والمدفوعات',
  ],
  [
    'and {1} more site, {2} days — ',
    'و{1} موقع آخر، {2} يوماً — ',
  ],
  [
    'and {1} more sites, {2} days — ',
    'و{1} مواقع أخرى، {2} يوماً — ',
  ],
  [
    'Charged to end-of-service',
    'محمَّل على نهاية الخدمة',
  ],
  [
    'Charged to end-of-service — {1}',
    'محمَّل على نهاية الخدمة — {1}',
  ],
  [
    'Plate {1} is already registered.',
    'اللوحة {1} مسجَّلة بالفعل.',
  ],
  [
    '{1}. Scans and trips are kept on the phone and sent when this comes back, '
    + 'so nothing is lost meanwhile.',
    '{1}. تُحفظ عمليات المسح والرحلات على الهاتف وتُرسل عند عودة الاتصال، فلا '
    + 'يضيع شيء في هذه الأثناء.',
  ],
  [
    'Company rules are incomplete — {1} setting(s) still unset: {2}',
    'قواعد الشركة غير مكتملة — {1} إعداداً ما زال غير محدد: {2}',
  ],

  /* ---- the settlement's line notes ---------------------------------------
     The small print beside each figure, saying how it was arrived at. Two
     versions of the leave note where there is a deduction and where there is
     not, and two of each again for the two bases a company can price leave on
     — because "basic" and "gross" are words inside the sentence rather than
     figures, and a shape keeps its words. */
  /* The older cycle, on its own line. See previousLeave in calc/settlement.ts:
     two figures a man can check rather than one he cannot. */
  [
    '{1} days from an earlier leave that was never paid, at {2} a day on basic',
    '{1} يوماً من إجازة سابقة لم تُدفع، بواقع {2} لليوم على الأساسي',
  ],
  [
    '{1} days from an earlier leave that was never paid, at {2} a day on gross',
    '{1} يوماً من إجازة سابقة لم تُدفع، بواقع {2} لليوم على الإجمالي',
  ],
  [
    '{1} days earned over {2} days worked, at {3} a day on basic',
    '{1} يوماً مستحقاً على {2} يوم عمل، بواقع {3} لليوم على الأساسي',
  ],
  [
    '{1} days earned over {2} days worked, at {3} a day on gross',
    '{1} يوماً مستحقاً على {2} يوم عمل، بواقع {3} لليوم على الإجمالي',
  ],
  [
    '{1} days earned over {2} days worked, less {3} days taken beyond the last '
    + 'approved leave = {4} days, at {5} a day on basic',
    '{1} يوماً مستحقاً على {2} يوم عمل، ناقص {3} يوماً أُخذت زيادة على آخر إجازة '
    + 'معتمدة = {4} يوماً، بواقع {5} لليوم على الأساسي',
  ],
  [
    '{1} days earned over {2} days worked, less {3} days taken beyond the last '
    + 'approved leave = {4} days, at {5} a day on gross',
    '{1} يوماً مستحقاً على {2} يوم عمل، ناقص {3} يوماً أُخذت زيادة على آخر إجازة '
    + 'معتمدة = {4} يوماً، بواقع {5} لليوم على الإجمالي',
  ],
  [
    '{1} days over {2} days of service',
    '{1} يوماً على {2} يوم خدمة',
  ],
  [
    '{1} days beyond the {2} approved — {3} month of visa cost',
    '{1} يوماً زيادة على {2} يوماً معتمدة — شهر واحد من تكلفة التأشيرة',
  ],
  [
    '{1} days beyond the {2} approved — {3} months of visa cost',
    '{1} يوماً زيادة على {2} يوماً معتمدة — {3} أشهر من تكلفة التأشيرة',
  ],
  [
    '{1} days of service ({2} years) is under the {3} year minimum, so no gratuity is due.',
    '{1} يوم خدمة ({2} سنة) أقل من الحد الأدنى البالغ {3} سنة، فلا تستحق مكافأة نهاية خدمة.',
  ],

  /* ---- what the settlement says ----------------------------------------- */
  [
    'The gratuity bands cover {1} of {2} days of service. {3} days fall outside '
    + 'every band and have earned nothing. Check the bands under Company rules.',
    'تغطي شرائح مكافأة نهاية الخدمة {1} من {2} يوم خدمة. و{3} يوماً تقع خارج كل '
    + 'شريحة ولم تستحق شيئاً. راجع الشرائح في قواعد الشركة.',
  ],
  [
    "Capped at {1} months' wages by the company rules.",
    'محدود بأجر {1} أشهر بموجب قواعد الشركة.',
  ],
  [
    '{1} day(s) of leave deducted: that much was taken beyond the last approved '
    + 'leave. The days come off the entitlement, not off pay.',
    'خُصم {1} يوماً من الإجازة: هذا القدر أُخذ زيادة على آخر إجازة معتمدة. '
    + 'تُخصم هذه الأيام من الاستحقاق لا من الأجر.',
  ],
  [
    "{1} was charged against this person's end-of-service, and the gratuity "
    + 'comes to {2}. The difference is still owed and is being recovered from '
    + 'the rest of the settlement.',
    'حُمّل {1} على مكافأة نهاية خدمة هذا الشخص، ومكافأة نهاية الخدمة تبلغ {2}. '
    + 'والفرق ما زال مستحقاً ويُسترد من باقي التسوية.',
  ],
  [
    'This settles at {1} — they have been paid {2} more than they have earned. '
    + 'Nothing will be handed over, and the balance has to be agreed with them '
    + 'before they go.',
    'تنتهي هذه التسوية عند {1} — فقد دُفع لهم {2} زيادة على ما استحقوه. لن '
    + 'يُسلَّم شيء، ولا بد من الاتفاق معهم على الرصيد قبل مغادرتهم.',
  ],
];

/**
 * Shape -> the template, both languages. A shape is the English with every {d}
 * as {n}, trimmed the way every lookup trims — untrimmed, the one entry ending
 * in a space ("and {1} more site, {2} days — ") could never be found.
 */
const BY_SHAPE = new Map(
  FIGURE_PAIRS.map(([en, ar]) => [en.trim().replace(/\{\d+\}/g, '{n}'), { en, ar }] as const),
);

/**
 * The Arabic for a sentence that still has its holes in it, or null.
 *
 *     s('{1} leave cycles').replace('{1}', '7')
 *
 * Most callers are written that way: the template looked up, the figures put
 * in after. The templates live in the figure table, and the figure table was
 * only ever reached through figureSentence — which lifts figures out of a
 * finished sentence, and so lifted the 1 out of "{1}" and went looking for
 * "{{n}} leave cycles". Every one of them came back in English: four lines and
 * the footer of an Arabic settlement, the net-salary line of an Arabic payroll
 * sheet, the sites note on a payslip. The guards checked that the shape was in
 * the table, which it always was. Nothing checked that asking for it found it.
 *
 * The Arabic numbers its holes the way the table's English does; they are
 * renumbered to the caller's, which are the ones the caller fills.
 */
function templateSentence(text: string): string | null {
  const holes = text.match(/\{\d+\}/g);
  if (!holes) return null;

  const entry = BY_SHAPE.get(text.trim().replace(/\{\d+\}/g, '{n}'));
  if (!entry) return null;

  const theirs = entry.en.match(/\{\d+\}/g) ?? [];
  const toCallers = new Map(theirs.map((hole, i) => [hole, holes[i] ?? hole]));
  return entry.ar.replace(/\{\d+\}/g, (hole) => toCallers.get(hole) ?? hole);
}

/**
 * The Arabic for a sentence whose figures are already inside it, or null.
 *
 * Exported for the test that checks no two templates share a shape — two that
 * did would make the lookup a coin toss, and the losing sentence would print
 * somebody else's numbers.
 */
export function figureSentence(text: string, lang: DocLang): string | null {
  if (lang !== 'ar') return null;

  const figures: string[] = [];
  const shape = text.trim().replace(FIGURE, (found) => {
    figures.push(found);
    return '{n}';
  });
  if (figures.length === 0) return null;

  const entry = BY_SHAPE.get(shape);
  if (entry === undefined) return null;

  /* By position, not in order: Arabic frequently wants them somewhere else in
     the sentence, and {2} may well come before {1}. Each goes back exactly as
     it came out, except a month, which goes back in Arabic — "Aug-26" in the
     middle of an Arabic sentence is an English word in it. */
  return entry.ar.replace(/\{(\d+)\}/g, (whole, digits: string) => {
    const figure = figures[Number(digits) - 1];
    return figure === undefined ? whole : docMonth(figure, lang);
  });
}

/** Every shape this file can fill — used by the test. */
export function figureShapes(): string[] {
  return [...BY_SHAPE.keys()];
}

const DOC_SENTENCES = new Map(SENTENCE_PAIRS);

/**
 * A sentence this system wrote, in the document's language.
 *
 * Untranslated sentences come back in English rather than blank, for the same
 * reason the labels do: a note that vanishes is a document that quietly says
 * less than it should.
 */
export function docSentence(text: string, lang: DocLang): string {
  if (lang !== 'ar') return text;
  return DOC_SENTENCES.get(text.trim())
    ?? templateSentence(text)
    ?? figureSentence(text, lang)
    ?? text;
}

/**
 * A remark the system attached to a line: a sentence as a rule, now and then a
 * single word, and sometimes several pieces joined with a dot.
 *
 * The notes under a settlement's totals are sentences — "242.69 days over 3756
 * days of service" — except the ticket's, which is its kind: "Air ticket", and
 * the payments', which are pieces: "2026-09-01 · bank transfer · Paid by
 * Yasir". Each piece is taken on its own: a stored date in the company's
 * shape and the document's month names, a sentence or a word this system
 * chose in the document's language, and anything else exactly as it was —
 * which is what keeps a note somebody typed out of the translator's hands.
 */
export function docRemark(text: string, lang: DocLang): string {
  const pieces = text.split(' · ');
  if (pieces.length > 1) return pieces.map((piece) => docRemark(piece.trim(), lang)).join('  ·  ');

  if (ISO_DATE.test(text.trim())) return formatDateAs(text.trim(), undefined, '—', lang);
  const sentence = docSentence(text, lang);
  return sentence !== text ? sentence : docLabel(text, lang);
}

/** Every note this file knows how to translate — used by the test. */
export function translatedSentences(): string[] {
  return [...DOC_SENTENCES.keys()];
}

/* -----------------------------------------------------------------------------
 * A cell on an exported document
 * -----------------------------------------------------------------------------
 * Two jobs, in this order. A stored date becomes the company's chosen shape —
 * every date on every screen goes through that, and a report printing raw ISO
 * was the one place the app disagreed with itself. Then the value is looked up
 * as a word this system chose: a status, a nationality, a job title.
 *
 * A value that is neither comes back exactly as it was given, and numbers are
 * not touched at all so a spreadsheet column can still be summed. That is the
 * point: a person's name, a site, a plate, a remark somebody typed must survive
 * an export untouched, because a plausible transliteration of a name is not the
 * name on the papers it will be checked against.
 *
 * The date part applies to English documents too — the shape a company writes
 * its dates in has nothing to do with which language it is writing in.
 * -------------------------------------------------------------------------- */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function docCell<T>(value: T, lang: DocLang): T | string {
  if (typeof value !== 'string') return value;

  const trimmed = value.trim();
  /* The arrangement is the company's; the month's name is the document's. A
     company that writes 22 Aug 2026 gets 22 أغسطس 2026 on an Arabic page —
     the same shape in its own words, rather than a different format or an
     English month sitting in an Arabic line. */
  if (ISO_DATE.test(trimmed)) return formatDateAs(trimmed, undefined, '—', lang);

  return docLabel(value, lang);
}

/* =============================================================================
 * The payroll's own remarks
 * -----------------------------------------------------------------------------
 * `payslips.remarks` holds the calculator's warnings, and the payroll function
 * writes them with `p_result ->> 'warnings'` — which on a JSON *array* hands
 * back the array's JSON text rather than its contents. So the column contains
 *
 *     ["One day was shorter than the standard day and is paid pro rata.",
 *      "Advance recovery reduced from 1000.00 to 515.14 by the 20% cap."]
 *
 * and the salary statement printed exactly that: brackets, quotes, commas, on a
 * line somebody is meant to read. Both sentences are worth reading; neither is
 * readable like that.
 *
 * Unpacked here rather than in a migration, because a migration would only fix
 * the rows written after it and every payslip already calculated would keep its
 * brackets. This repairs what is there, and what will be written tomorrow.
 *
 * Each sentence is then translated whole, the way report notes are — they are
 * sentences this system wrote, and it knows them.
 * ========================================================================== */
export function docRemarks(raw: string | null | undefined, lang: DocLang): string {
  const text = (raw ?? '').trim();
  if (!text) return '';

  /* Only a JSON array is unpacked. A remark somebody typed by hand that happens
     to start with a bracket stays exactly as it was typed. */
  if (text.startsWith('[')) {
    try {
      const parts = JSON.parse(text) as unknown;
      if (Array.isArray(parts)) {
        return parts
          .filter((part): part is string => typeof part === 'string')
          .map((part) => docSentence(part, lang))
          .join('  ·  ');
      }
    } catch {
      // Not JSON after all. Printed as it stands, which is what it is.
    }
  }

  return docSentence(text, lang);
}
